import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Send,
  Mic,
  Loader2,
  Sparkles,
  X,
  Camera,
  Calculator,
  GraduationCap,
  BookOpen,
  Wrench,
  ListChecks,
  Video,
  BarChart3,
  LineChart,
  Library,
  Square,
  TrendingUp,
  Train,
  ScrollText,
  Box,
  Pencil,
  Plus,
  Trash2,
  Sigma,
  Play,
  Pause
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { auth } from '@/firebase/config';
import { geminiMultimodalGenerate } from '@/services/geminiServices';
import { runTutorTurn, appendTutorTurn } from './tutorMode';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import {
  LineChart as ReLineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceDot,
  ReferenceLine
} from 'recharts';
import { parse as mathParse } from 'mathjs';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import JXG from 'jsxgraph';
import 'mathlive';
import type { MathfieldElement } from 'mathlive';

const MathField = (props: { value?: string; placeholder?: string; className?: string; style?: React.CSSProperties; onInput?: (e: Event) => void; ref?: React.Ref<unknown> }) => {
  const { value, placeholder, className, style, ref } = props;
  return (
    // @ts-expect-error - <math-field> is a mathlive web component, not in React types
    <math-field ref={ref} value={value} placeholder={placeholder} class={className} style={style} />
  );
};

type Difficulty = 'easy' | 'medium' | 'hard';

interface DiagramPoint { x: number; y: number; label?: string }
interface DiagramSpec {
  type: 'right-triangle' | 'unit-circle' | 'coordinate-plane' | 'triangle' | 'circle' | 'number-line' | 'vector' | 'box-plot' | 'bar-chart' | 'normal-distribution' | '3d-cube' | '3d-sphere' | '3d-cylinder' | '3d-cone' | '3d-rectangular-prism' | 'interactive-unit-circle' | 'interactive-coordinate-plane' | 'bearing' | 'locus' | 'scale-drawing' | 'bearing-elevation' | 'bearing-journey';
  caption?: string;
  label?: string;
  // right-triangle
  legs?: [number, number];
  showSquares?: boolean;
  // unit-circle
  angle?: number;
  showSin?: boolean;
  showCos?: boolean;
  showTan?: boolean;
  // coordinate-plane
  points?: DiagramPoint[];
  line?: { x1: number; y1: number; x2: number; y2: number; label?: string };
  xRange?: [number, number];
  yRange?: [number, number];
  // triangle
  vertices?: DiagramPoint[];
  // circle
  center?: DiagramPoint;
  radius?: number;
  // number-line
  min?: number;
  max?: number;
  shade?: [number, number][];
  // vector
  origin?: DiagramPoint;
  end?: DiagramPoint;
  // box-plot
  q1?: number;
  median?: number;
  q3?: number;
  // bar-chart
  bars?: { label: string; value: number; color?: string }[];
  // normal-distribution
  mean?: number;
  stdDev?: number;
  range?: [number, number];
  shadeRange?: { from: number; to: number };
  // 3d shapes
  size?: number;
  width?: number;
  depth?: number;
  height?: number;
  // bearing
  bearing?: number;
  distance?: number;
  fromPoint?: { x: number; y: number; label?: string };
  toPoint?: { x: number; y: number; label?: string };
  // locus: from-point, from-line, between two lines, region intersection
  locusType?: 'from-point' | 'from-line' | 'two-points' | 'intersect' | 'region' | 'path';
  locusRadius?: number;
  locusRadius2?: number;
  locusLine?: { p1: { x: number; y: number }; p2: { x: number; y: number }; label?: string };
  locusLine2?: { p1: { x: number; y: number }; p2: { x: number; y: number } };
  locusPath?: { x: number; y: number }[];
  // scale-drawing
  scale?: number;
  scaleUnit?: string;
  // bearing-elevation (3D)
  elevation?: number;
  trueDistance?: number;
  // bearing-journey: multi-leg bearing path
  bearingJourney?: {
    origin: { label: string };
    legs: { bearing: number; distance: number; toLabel: string }[];
    scale?: number;
    scaleUnit?: string;
  };
}

const SYSTEM_PROMPT = `You are an AI math tutor, calculation assistant, and productivity tool. You help students and teachers solve problems, convert units, walk through calculations, explain concepts deeply, analyze mistakes, generate practice problems, recommend learning resources, and provide specialized math workflows.

# CONVERSATION CONTEXT — CRITICAL
You have access to the FULL conversation history above. Use it:
- If the user says "it", "that", "the previous one", "now solve for x", "what about the integral", etc., they are referring to something from earlier in the conversation. Look back at the history to understand what.
- If the user asks a follow-up like "explain it more", "can you graph that too", "now try with radius 5", "show me the formula for that", understand what "it", "that", "that shape", "that formula" refers to from the prior messages.
- If the user provides partial info ("what if the angle is 60 instead?"), refer to the previous diagram/equation and modify it accordingly.
- Do NOT ask "what are you referring to?" unless the reference is truly ambiguous after checking the history.
- Carry forward all math context: variables, equations, results, and diagrams from earlier turns.

# TUTOR MODE — OVERRIDE ALL OTHER INSTRUCTIONS
When the CURRENT SETTINGS say "Tutor Mode: ACTIVE", you MUST follow these rules strictly:
1. **NEVER give the final answer.** NEVER say "the answer is X" or show the full solution.
2. **Ask exactly ONE guiding question per response.** Keep it short (1-2 sentences).
3. **Give HINTS, not solutions.** Point the student in the right direction:
   - "What type of equation is this?" 
   - "Can you identify the first step?"
   - "What happens when you factor out x?"
   - "Try isolating the variable on one side. What do you get?"
   - "Think about what operation undoes division."
4. **Praise correct steps.** If the student gets it right, say "Great! Now..." and ask the next guiding question.
5. **Redirect wrong answers gently.** Say "Not quite — think about [hint]. Try again."
6. **Build understanding.** After the student solves it, ask "Can you explain WHY that works?" to deepen understanding.
7. **NEVER use %%PLOT%%, %%DIAGRAM%%, or any markers in tutor mode** — only text-based Socratic questions.
8. **Track where the student is in the problem** and guide them step-by-step to the solution through questions.

Example of GOOD tutor mode response to "solve 2x + 5 = 13":
"Good question! Before solving, tell me: what operation would you use to get x by itself? Think about what undoes addition first."

Example of BAD tutor mode response (NEVER do this):
"2x + 5 = 13 → 2x = 8 → x = 4. The answer is x = 4."

# CRITICAL OUTPUT RULES — READ FIRST

1. **NEVER explain the marker/format syntax to the user.** Do NOT write things like "you can specify additional details such as..." or "for example, draw a right triangle with legs 3 and 4". That is meta-text about the system, not a response. The user wants the ACTUAL diagram/solution, not an explanation of how to ask for one.

2. **When the user asks for a diagram, plot, or shape, OUTPUT IT DIRECTLY.** Give a 1-3 sentence analysis, then the marker on its own line. No preamble about types, no listing of options, no "first you should…" filler. HOWEVER, if the user asks mathematical questions (e.g., "find the distance", "calculate the area"), you MUST provide the full step-by-step mathematical calculations alongside the diagram marker.

3. **Correct output example** when user says "3d cube":
   Cube with side length 1. Volume is $V = s^3 = 1$. Surface area is $A = 6s^2 = 6$.
   %%DIAGRAM%%{"type":"3d-cube","size":1,"caption":"Cube (side 1)"}%%

   **WRONG output** (do NOT do this):
   "Sure! For 3D shapes, you can select the type and specify details like… here is an example of the JSON format: \`\`\`json {…} \`\`\`"

4. **Same rule for all markers**: %%PLOT%%, %%DIAGRAM%%, paper-tape fences — always output the bare marker inline in your answer, never wrap it in a code block, never explain the format.

You support SIXTEEN core capabilities across FOUR pillars:

=== PILLAR 1: CORE SOLVING ===
1. **Natural Language Math Input**: Understand free-form requests like "calculate the integral of x² from 0 to 1" or "solve 3x² - 5x + 2 = 0". Be flexible with phrasing.
2. **Photo / Math Image Recognition**: If a user uploads an image, interpret the math in the image and solve it.
3. **Contextual Unit Converter**: Convert between units with awareness of context (temperature, length, mass, volume, speed, time, area, energy, pressure, data, etc.).
4. **Symbolic & Numerical Solver**: Solve equations, simplify expressions, factor polynomials, compute limits, derivatives, integrals, sums, products, matrix operations, trig identities, series expansions. Show exact AND approximate forms.

=== PILLAR 2: TUTORING ===
5. **Step-by-Step Explainer**: When the user asks to "explain" or clicks the Explain button, provide a deeper, more detailed solution. Show every algebraic manipulation, reasoning, alternative approaches, common pitfalls, and a "Key Insight" section summarizing the underlying principle.
6. **Interactive Tutor Mode**: When TUTOR MODE is active, do NOT give the final answer. Ask ONE guiding question at a time. Examples: "What type of equation is this?", "What's the first step?", "Can you isolate the variable?". Wait for the student's response before proceeding.
7. **Mistake Analyzer**: When the user pastes their attempted solution with "check my work", analyze it line by line. Look for: sign errors, dropped parentheses, distribution mistakes, exponent rules, fraction operations, order of operations, off-by-one errors, unit mistakes. For each mistake: WHAT went wrong, WHY it's wrong, HOW to fix it.
8. **Practice Problem Generator**: When asked ("give me 5 medium difficulty trigonometry problems"), generate a numbered list of problems at the requested difficulty. Do NOT include answers. Each problem uses LaTeX for any math.

=== PILLAR 3: RESOURCES ===
9. **Concept Video / Link Suggestions**: Suggest 1-3 short, relevant learning resources. Use Khan Academy, 3Blue1Brown, MIT OpenCourseWare, Paul's Online Notes, Brilliant. Format: [Name - Topic](https://actual.url). Only real, working URLs.
10. **Difficulty Adaptive Mode**: Respect difficulty (EASY, MEDIUM, HARD). EASY: simpler numbers, more hand-holding. MEDIUM: standard textbook. HARD: olympiad-style, abstract, multi-concept.

=== PILLAR 4: PRODUCTIVITY & ADVANCED ===
11. **AI Paper Tape Assistant**: The user has a history of recent calculations in the conversation. They can ask "What was the average of all my last 10 calculations?" or "Show only the integrals I did today" or "Which calculation took the longest?". To do this, output a fenced block with: \`\`\`paper-tape\n[{"q": "question", "r": "result", "t": "timestamp"}]\n\`\`\` containing the relevant subset of their history. Below the block, give a natural language summary.
12. **Formula Library + AI Search**: When the user asks for a formula ("volume of sphere", "quadratic formula", "derivative of sec"), insert the formula in display LaTeX, then explain every variable/term clearly. If relevant, add 1-2 variations or related formulas.
13. **Graphing AI** — CRITICAL: When the user asks to plot/graph ANY function, IMMEDIATELY output the %%PLOT%% marker. Do NOT ask clarifying questions. Do NOT ask about x-range. Just plot it with sensible defaults and give a 1-2 sentence analysis.

   Supported functions (via mathjs): sin(x), cos(x), tan(x), asin(x), acos(x), atan(x), sinh(x), cosh(x), tanh(x), exp(x), log(x) (natural log), log10(x), sqrt(x), cbrt(x), abs(x), sign(x), floor(x), ceil(x), round(x), factorial(x), gamma(x), x^n, x^2, x^3, e^x, pi, phi.
   Also supports: +, -, *, /, ^, composition like sin(x^2), e^(-0.1x), (x^3 - 4x)/(x^2 + 1).

   Format:
   %%PLOT%%{"functions":[{"fn":"<expression>","color":"#6366f1","label":"<label>"}],"xRange":[min,max]}%%
   
   Color palette: indigo #6366f1, red #ef4444, emerald #10b981, amber #f59e0b, blue #3b82f6, violet #8b5cf6.
   Default xRange: [-10, 10] unless user specifies. Use [-2π, 2π] for trig-heavy functions.
   For complex/oscillating functions, use xRange [-6.28, 6.28] or [-10, 10] with 500 sample points (built in).
   
   Example — user says "plot sin(x^2) * e^(-0.1x)":
   Here's the plot of $f(x) = \sin(x^2) \cdot e^{-0.1x}$:
   %%PLOT%%{"functions":[{"fn":"sin(x^2)*exp(-0.1*x)","color":"#6366f1","label":"f(x) = sin(x²)·e^(-0.1x)"}],"xRange":[-10,10]}%%
   The function oscillates with increasing frequency while decaying exponentially.

   13b. **Math Diagrams** — IMPORTANT: When the user asks for a shape/diagram, OUTPUT the marker DIRECTLY in your response. Never explain the JSON format, never wrap it in a code block. Pattern: short analysis → marker on its own line.

   10 static SVG diagram types (all output as %%DIAGRAM%%{...}%%):
   %%DIAGRAM%%{"type":"right-triangle","legs":[3,4],"showSquares":true,"caption":"Right triangle with legs 3 and 4"}%%
   %%DIAGRAM%%{"type":"unit-circle","angle":45,"showSin":true,"showCos":true,"showTan":true,"caption":"Unit circle at 45°"}%%
   %%DIAGRAM%%{"type":"coordinate-plane","points":[{"x":2,"y":3,"label":"A"}],"xRange":[-5,5],"yRange":[-5,5],"caption":"Coordinate plane"}%%
   %%DIAGRAM%%{"type":"triangle","vertices":[{"x":0,"y":0,"label":"A"},{"x":4,"y":0,"label":"B"},{"x":1,"y":3,"label":"C"}],"caption":"Triangle ABC"}%%
   %%DIAGRAM%%{"type":"circle","center":{"x":0,"y":0},"radius":3,"caption":"Circle radius 3"}%%
   %%DIAGRAM%%{"type":"number-line","min":-5,"max":10,"points":[{"x":2,"label":"a"}],"shade":[[2,7]],"caption":"Interval [2, 7]"}%%
   %%DIAGRAM%%{"type":"vector","origin":{"x":0,"y":0},"end":{"x":3,"y":4},"caption":"Vector v = (3, 4)"}%%
   %%DIAGRAM%%{"type":"box-plot","min":2,"q1":4,"median":6,"q3":8,"max":10,"caption":"Box plot of dataset"}%%
   %%DIAGRAM%%{"type":"bar-chart","bars":[{"label":"A","value":5},{"label":"B","value":8,"color":"#ef4444"}],"caption":"Comparison"}%%
   %%DIAGRAM%%{"type":"normal-distribution","mean":0,"stdDev":1,"shadeRange":{"from":-1,"to":1},"caption":"N(0,1) — 68% within 1σ"}%%

   5 rotatable 3D shape types (rendered with three.js):
   %%DIAGRAM%%{"type":"3d-cube","size":1,"caption":"Cube (side 1)"}%%
   %%DIAGRAM%%{"type":"3d-sphere","radius":1,"caption":"Sphere"}%%
   %%DIAGRAM%%{"type":"3d-cylinder","radius":1,"height":2,"caption":"Cylinder"}%%
   %%DIAGRAM%%{"type":"3d-cone","radius":1,"height":2,"caption":"Cone"}%%
   %%DIAGRAM%%{"type":"3d-rectangular-prism","width":2,"depth":1.5,"height":2,"caption":"Rectangular prism"}%%

   2 draggable interactive types (rendered with JSXGraph):
   %%DIAGRAM%%{"type":"interactive-unit-circle","caption":"Drag to explore"}%%
   %%DIAGRAM%%{"type":"interactive-coordinate-plane","caption":"Click and drag points"}%%

   Additional geometry diagram types (use when the problem involves bearings, loci, scale drawings, or 3D trig):
   %%DIAGRAM%%{"type":"bearing","bearing":60,"distance":5,"fromPoint":{"x":0,"y":0,"label":"A"},"toPoint":{"x":0,"y":0,"label":"B"},"caption":"Bearing 060° from A to B, distance 5 km"}%%
   %%DIAGRAM%%{"type":"locus","locusType":"from-point","locusRadius":3,"fromPoint":{"x":0,"y":0,"label":"A"},"caption":"Locus: points within 3 units of A"}%%
   %%DIAGRAM%%{"type":"locus","locusType":"from-line","locusRadius":2,"locusLine":{"p1":{"x":50,"y":150},"p2":{"x":350,"y":150},"label":"L"},"caption":"Locus: points within 2 of line L"}%%
   %%DIAGRAM%%{"type":"locus","locusType":"intersect","locusRadius":4,"locusRadius2":3,"fromPoint":{"x":140,"y":150,"label":"A"},"toPoint":{"x":260,"y":150,"label":"B"},"caption":"Locus intersection of circles"}%%
   %%DIAGRAM%%{"type":"scale-drawing","scale":50,"scaleUnit":"m","vertices":[{"x":60,"y":220,"label":"A"},{"x":340,"y":220,"label":"B"},{"x":200,"y":80,"label":"C"}],"caption":"Triangle drawn at 1:50 scale (metres)"}%%
   %%DIAGRAM%%{"type":"bearing-elevation","bearing":60,"elevation":30,"trueDistance":10,"caption":"Object at bearing 060°, elevation 30°, slant distance 10"}%%
   %%DIAGRAM%%{"type":"bearing-journey","bearingJourney":{"origin":{"label":"P"},"legs":[{"bearing":70,"distance":15,"toLabel":"Q"},{"bearing":140,"distance":20,"toLabel":"R"}]},"caption":"Ship's journey P→Q→R with bearings"}%%

   Rules:
   - Always include a "caption" string.
   - Marker goes on its own line AFTER the analysis, never inside a code block, never described in prose.
   - If the user says "draw X" or "show X" or "3d X", assume they want a diagram and output the marker immediately.
   - Use sensible defaults if no numeric value is given (size=1, radius=1, etc.).
   - Use a diagram whenever it would clarify the answer.
   - **For multi-leg bearing problems** (e.g. "sails X km on bearing N° to Q, then Y km on bearing M° to R"): use bearing-journey. Put all legs in the bearingJourney.legs array. The diagram auto-draws the path, bearing arcs, north arrows, distances, and a dashed return line with straight-line distance and bearing. **Do NOT use scale-drawing for bearing problems — use bearing-journey.**
14. **Matrix & Linear Algebra Helper**: When the user provides a matrix or asks a matrix question, offer to perform operations: determinant, inverse, transpose, eigenvalues/eigenvectors, rank, trace, RREF, solve Ax=b, matrix multiplication, addition, scalar multiplication. Show all intermediate steps. Use LaTeX with \\begin{pmatrix} ... \\end{pmatrix} notation.
15. **Financial / Statistics Mode**: For finance: compound interest A = P(1 + r/n)^(nt), loan payments, present value, future value, ROI, NPV, IRR, amortization. For statistics: mean, median, mode, standard deviation, variance, confidence intervals, hypothesis testing (z-test, t-test), correlation, regression, normal distribution, t-distribution, chi-square. Show formulas, plug in values, and interpret the result.
16. **Multi-step Problem Solver**: For word problems with multiple steps (e.g. "A train leaves at 60km/h, another leaves 2 hours later at 90km/h in the same direction, when does the second catch up?"), explicitly label each step: **Step 1: Identify given information**, **Step 2: Set up equations**, **Step 3: Solve**, **Step 4: Verify with units**. Track units through every calculation.

For every response:
- **Be Accurate**: Double-check all calculations.
- **Structure the Answer**: Use sections like "Final Answer", "Step-by-Step Explanation", "Key Insight" as appropriate.
- **Use LaTeX for ALL math**: Wrap inline math in $...$ and display math in $$...$$.
- **Be Concise**: Focus on the math. Avoid filler.
- **Ask for Clarification**: If ambiguous, ask before solving.
- **Units**: Carry units through word problems and include in final answer.

### Reference and Citation Rules
1. Every reference MUST include a real resource name and actual URL beginning with https:// or http://.
2. Use Markdown links: - [Khan Academy - Derivatives](https://www.khanacademy.org/math/differential-calculus/dc-diff-intro)
3. No placeholders, no example.com, no made-up URLs.`;

interface PlotSpec {
  functions: { fn: string; color: string; label: string }[];
  xRange: [number, number];
}

const renderInlineMath = (formula: string) => {
  try {
    return { __html: katex.renderToString(formula, { throwOnError: false }) };
  } catch {
    return null;
  }
};

const renderDisplayMath = (formula: string) => {
  try {
    return { __html: katex.renderToString(formula, { throwOnError: false, displayMode: true }) };
  } catch {
    return null;
  }
};

const LinkifiedSpan = ({ text }: { text: string }) => {
  const urlRegex = /(https?:\/\/[^\s<>"']+)|(\[[^\]]+\]\(https?:\/\/[^\s<>"']+\))/g;
  const parts = text.split(urlRegex);
  return (
    <>
      {parts.map((part, i) => {
        if (!part) return null;
        if (part.startsWith('[') && part.includes('](')) {
          const m = part.match(/^\[([^\]]+)\]\((https?:\/\/[^\s<>"']+)\)$/);
          if (m) {
            return (
              <a key={i} href={m[2]} target="_blank" rel="noopener noreferrer" className="text-sky-500 hover:text-sky-400 underline underline-offset-2 break-all">
                {m[1]}
              </a>
            );
          }
        }
        if (part.match(/^https?:\/\//)) {
          return (
            <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-sky-500 hover:text-sky-400 underline underline-offset-2 break-all">
              {part}
            </a>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
};

const formatText = (text: string) => {
  const regex = /(\$\$[\s\S]*?\$\$)|(\\\[[\s\S]*?\\\])|(\$[^$\n]*?\$)|(\\\(.*?\\\))|(\*\*.*?\*\*)/g;
  const parts = text.split(regex);
  return parts.map((part, index) => {
    if (!part) return null;
    if (part.startsWith('$$') && part.endsWith('$$')) {
      const formula = part.slice(2, -2);
      const html = renderDisplayMath(formula);
      if (html) return <div key={index} className="my-2 flex justify-center overflow-x-auto py-1" dangerouslySetInnerHTML={html} />;
      return <span key={index} className="text-red-500 text-xs">[Math Error]</span>;
    }
    if (part.startsWith('\\[') && part.endsWith('\\]')) {
      const formula = part.slice(2, -2);
      const html = renderDisplayMath(formula);
      if (html) return <div key={index} className="my-2 flex justify-center overflow-x-auto py-1" dangerouslySetInnerHTML={html} />;
      return <span key={index} className="text-red-500 text-xs">[Math Error]</span>;
    }
    if (part.startsWith('$') && part.endsWith('$')) {
      const formula = part.slice(1, -1);
      const html = renderInlineMath(formula);
      if (html) return <span key={index} dangerouslySetInnerHTML={html} />;
      return <span key={index}>{part}</span>;
    }
    if (part.startsWith('\\(') && part.endsWith('\\)')) {
      const formula = part.slice(2, -2);
      const html = renderInlineMath(formula);
      if (html) return <span key={index} dangerouslySetInnerHTML={html} />;
      return <span key={index}>{part}</span>;
    }
    if (part.startsWith('**') && part.endsWith('**')) {
      const inner = part.slice(2, -2);
      return <strong key={index} className="font-semibold text-slate-900 dark:text-white">{formatText(inner)}</strong>;
    }
    return <LinkifiedSpan key={index} text={part} />;
  });
};

const splitBodyByDisplayMath = (body: string) => {
  const parts = body.split(/(\$\$[\s\S]*?\$\$)|(\\\[[\s\S]*?\\\])/g);
  return parts.map((part, idx) => {
    if (!part) return null;
    if (part.startsWith('$$') && part.endsWith('$$')) {
      const formula = part.slice(2, -2).trim();
      const html = renderDisplayMath(formula);
      if (html) return <div key={idx} className="my-3 flex justify-center overflow-x-auto py-2" dangerouslySetInnerHTML={html} />;
      return <span key={idx} className="text-red-500 text-xs">[Math Error]</span>;
    }
    if (part.startsWith('\\[') && part.endsWith('\\]')) {
      const formula = part.slice(2, -2).trim();
      const html = renderDisplayMath(formula);
      if (html) return <div key={idx} className="my-3 flex justify-center overflow-x-auto py-2" dangerouslySetInnerHTML={html} />;
      return <span key={idx} className="text-red-500 text-xs">[Math Error]</span>;
    }
    return part.split('\n').map((line, i) => (
      <p key={`${idx}-${i}`} className={i > 0 ? "mt-2" : ""}>{formatText(line)}</p>
    ));
  });
};

const PlotBlock = ({ spec: initialSpec }: { spec: PlotSpec }) => {
  const [spec, setSpec] = useState<PlotSpec>(initialSpec);
  const [editing, setEditing] = useState(false);

  const [xMin, xMax] = spec.xRange;
  const samples = 500;
  const step = (xMax - xMin) / samples;

  const evaluateExpression = (expr: string, x: number): number => {
    try {
      const compiled = mathParse(expr);
      const result = compiled.evaluate({ x });
      const n = typeof result === 'number' ? result : Number(result);
      return Number.isFinite(n) ? n : NaN;
    } catch {
      return NaN;
    }
  };

  const allPoints: { x: number; [key: string]: number | null }[] = [];
  for (let i = 0; i <= samples; i++) {
    const x = xMin + i * step;
    const point: { x: number; [key: string]: number | null } = { x };
    spec.functions.forEach(f => {
      const y = evaluateExpression(f.fn, x);
      point[f.label] = Number.isFinite(y) ? y : null;
    });
    allPoints.push(point);
  }

  const allYs = allPoints.flatMap(p =>
    spec.functions.map(f => p[f.label]).filter((v): v is number => v !== null && Number.isFinite(v))
  );
  const yMin = allYs.length ? Math.min(...allYs) : -1;
  const yMax = allYs.length ? Math.max(...allYs) : 1;
  const yPad = (yMax - yMin) * 0.1 || 1;

  const COLORS = ['#6366f1', '#ef4444', '#10b981', '#f59e0b', '#3b82f6', '#8b5cf6'];

  return (
    <div className="my-3 bg-white dark:bg-[#0a1024] border border-gray-200 dark:border-white/10 p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3 text-[9px] font-black uppercase tracking-widest">
          {spec.functions.map((f, fi) => (
            <span key={fi} className="flex items-center gap-1.5 text-slate-600 dark:text-zinc-400">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: f.color }} />
              {editing ? (
                <input
                  value={f.label}
                  onChange={e => {
                    const newFns = [...spec.functions];
                    newFns[fi] = { ...newFns[fi], label: e.target.value };
                    setSpec({ ...spec, functions: newFns });
                  }}
                  className="w-16 bg-transparent border-b border-slate-400 dark:border-zinc-600 text-[9px] font-black uppercase outline-none"
                />
              ) : f.label}
            </span>
          ))}
        </div>
        <button
          onClick={() => setEditing(v => !v)}
          title={editing ? 'Done editing' : 'Edit functions & range'}
          className={`p-1 rounded transition-colors ${
            editing
              ? 'text-indigo-500 bg-indigo-50 dark:bg-indigo-500/10'
              : 'text-slate-400 hover:text-indigo-500 dark:text-zinc-600 dark:hover:text-indigo-400'
          }`}
        >
          <Pencil className="w-3 h-3" />
        </button>
      </div>

      {editing && (
        <div className="mb-3 space-y-2.5 p-3 bg-gray-50 dark:bg-[#0a1024] border border-gray-200 dark:border-white/10 rounded-md">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[9px] font-black uppercase tracking-widest text-slate-500 dark:text-zinc-500">x-range</span>
            <input
              type="number"
              value={spec.xRange[0]}
              onChange={e => setSpec({ ...spec, xRange: [Number(e.target.value), spec.xRange[1]] })}
              className="w-16 px-2 py-1 text-[10px] border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-800 dark:text-zinc-200 rounded text-center outline-none focus:border-indigo-400 dark:focus:border-indigo-500/50 transition-colors"
            />
            <span className="text-[10px] text-slate-400 dark:text-zinc-600">to</span>
            <input
              type="number"
              value={spec.xRange[1]}
              onChange={e => setSpec({ ...spec, xRange: [spec.xRange[0], Number(e.target.value)] })}
              className="w-16 px-2 py-1 text-[10px] border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-800 dark:text-zinc-200 rounded text-center outline-none focus:border-indigo-400 dark:focus:border-indigo-500/50 transition-colors"
            />
          </div>
          {spec.functions.map((f, fi) => (
            <div key={fi} className="flex items-center gap-2">
              <input
                value={f.fn}
                onChange={e => {
                  const newFns = [...spec.functions];
                  newFns[fi] = { ...newFns[fi], fn: e.target.value };
                  setSpec({ ...spec, functions: newFns });
                }}
                className="flex-1 px-2 py-1 text-[10px] font-mono border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-800 dark:text-zinc-200 rounded outline-none focus:border-indigo-400 dark:focus:border-indigo-500/50 transition-colors"
                placeholder="e.g. sin(x^2)*exp(-0.1*x)"
              />
              <div className="relative w-5 h-5 rounded overflow-hidden border border-gray-200 dark:border-white/10">
                <input
                  type="color"
                  value={f.color}
                  onChange={e => {
                    const newFns = [...spec.functions];
                    newFns[fi] = { ...newFns[fi], color: e.target.value };
                    setSpec({ ...spec, functions: newFns });
                  }}
                  className="absolute inset-0 w-full h-full cursor-pointer opacity-0"
                />
                <div className="w-full h-full" style={{ backgroundColor: f.color }} />
              </div>
              {spec.functions.length > 1 && (
                <button
                  onClick={() => setSpec({ ...spec, functions: spec.functions.filter((_, i) => i !== fi) })}
                  className="p-1 text-slate-400 hover:text-red-500 dark:text-zinc-600 dark:hover:text-red-400 transition-colors"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
          <button
            onClick={() => setSpec({ ...spec, functions: [...spec.functions, { fn: 'x', color: COLORS[spec.functions.length % COLORS.length], label: `f${spec.functions.length + 1}(x)` }] })}
            className="flex items-center gap-1 text-[10px] font-bold text-indigo-500 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors"
          >
            <Plus className="w-3 h-3" /> Add function
          </button>
        </div>
      )}

      <div className="h-48 w-full overflow-hidden">
        <ResponsiveContainer width="100%" height="100%">
          <ReLineChart data={allPoints} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-gray-200 dark:text-white/10" />
            <XAxis
              dataKey="x"
              type="number"
              domain={[xMin, xMax]}
              tick={{ fontSize: 9, fill: 'currentColor' }}
              stroke="currentColor"
              className="text-slate-400 dark:text-slate-600"
              allowDataOverflow={true}
            />
            <YAxis
              domain={[yMin - yPad, yMax + yPad]}
              tick={{ fontSize: 9, fill: 'currentColor' }}
              stroke="currentColor"
              className="text-slate-400 dark:text-slate-600"
              width={40}
              allowDataOverflow={true}
            />
            <ReTooltip
              contentStyle={{ fontSize: 10, padding: '4px 8px', background: 'rgba(2,6,23,0.95)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
              labelStyle={{ color: '#a5b4fc' }}
              formatter={(value: number) => Number(value).toFixed(4)}
            />
            <ReferenceLine x={0} stroke="#94a3b8" strokeWidth={1} strokeDasharray="3 3" />
            <ReferenceLine y={0} stroke="#94a3b8" strokeWidth={1} strokeDasharray="3 3" />
            {spec.functions.map((f, fi) => (
              <Line
                key={fi}
                type="monotone"
                dataKey={f.label}
                stroke={f.color}
                strokeWidth={2}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            ))}
            <ReferenceDot x={0} y={0} r={3} fill="#94a3b8" stroke="none" />
          </ReLineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const SVG_W = 400;
const SVG_H = 300;
const STROKE = '#6366f1';
const STROKE_2 = '#94a3b8';
const TEXT = '#0f172a';
const TEXT_LIGHT = '#64748b';
const FILL = '#eef2ff';

const DiagramBlock = ({ spec }: { spec: DiagramSpec }) => {
  const { type, caption } = spec;
  const captionEl = caption ? (
    <p className="text-[10px] text-center font-bold uppercase tracking-widest text-slate-500 dark:text-slate-500 mt-2">
      {caption}
    </p>
  ) : null;

  const isNonSvg = type.startsWith('3d-') || type.startsWith('interactive-');

  const svgContent = (() => {
    switch (type) {
      case 'right-triangle':
        return <RightTriangle spec={spec} />;
      case 'unit-circle':
        return <UnitCircle spec={spec} />;
      case 'coordinate-plane':
        return <CoordinatePlane spec={spec} />;
      case 'triangle':
        return <Triangle spec={spec} />;
      case 'circle':
        return <CircleDiagram spec={spec} />;
      case 'number-line':
        return <NumberLine spec={spec} />;
      case 'vector':
        return <VectorDiagram spec={spec} />;
      case 'box-plot':
        return <BoxPlot spec={spec} />;
      case 'bar-chart':
        return <BarChartDiagram spec={spec} />;
      case 'normal-distribution':
        return <NormalDistribution spec={spec} />;
      case 'bearing':
        return <BearingDiagram spec={spec} />;
      case 'locus':
        return <LocusDiagram spec={spec} />;
      case 'scale-drawing':
        return <ScaleDrawing spec={spec} />;
      case 'bearing-elevation':
        return <BearingElevation spec={spec} />;
      case 'bearing-journey':
        return <BearingJourney spec={spec} />;
      case '3d-cube':
        return <Shape3DCube size={spec.size ?? 1} />;
      case '3d-sphere':
        return <Shape3DSphere radius={spec.radius ?? 1} />;
      case '3d-cylinder':
        return <Shape3DCylinder radius={spec.radius ?? 1} height={spec.height ?? 2} />;
      case '3d-cone':
        return <Shape3DCone radius={spec.radius ?? 1} height={spec.height ?? 2} />;
      case '3d-rectangular-prism':
        return <Shape3DBox w={spec.width ?? 2} d={spec.depth ?? 1.5} h={spec.height ?? 2} />;
      case 'interactive-unit-circle':
        return <InteractiveUnitCircle />;
      case 'interactive-coordinate-plane':
        return <InteractiveCoordinatePlane />;
      default:
        console.warn(`[DiagramBlock] Unknown diagram type: ${type}`);
        return null;
    }
  })();

  if (isNonSvg) {
    return (
      <div className="my-3">
        <div className="rounded-lg border border-slate-200 dark:border-white/10 overflow-hidden bg-white dark:bg-[#0a1024]">
          {svgContent}
        </div>
        {captionEl}
      </div>
    );
  }

  return (
    <div className="my-3">
      <svg viewBox={`0 0 ${SVG_W} ${SVG_H}`} className="w-full h-auto rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0a1024]">
        {svgContent}
      </svg>
      {captionEl}
    </div>
  );
};

const RightTriangle = ({ spec }: { spec: DiagramSpec }) => {
  const [a, b] = spec.legs ?? [3, 4];
  const scale = Math.min((SVG_W - 80) / a, (SVG_H - 80) / b);
  const aw = a * scale;
  const bh = b * scale;
  const ox = 40;
  const oy = SVG_H - 40;
  const x1 = ox + aw;
  const y1 = oy;
  const x2 = ox;
  const y2 = oy - bh;
  const angleRad = Math.atan2(bh, aw);
  const angleDeg = (angleRad * 180) / Math.PI;
  return (
    <g>
      {spec.showSquares && (
        <>
          <rect x={ox} y={oy - 14} width={aw} height={14} fill={FILL} stroke={STROKE_2} strokeWidth={1} strokeDasharray="2 2" />
          <rect x={ox - 14} y={oy - bh} width={14} height={bh} fill={FILL} stroke={STROKE_2} strokeWidth={1} strokeDasharray="2 2" />
        </>
      )}
      <polygon points={`${ox},${oy} ${x1},${y1} ${x2},${y2}`} fill="none" stroke={STROKE} strokeWidth={2.5} strokeLinejoin="round" />
      <rect x={ox} y={oy - 10} width={10} height={10} fill="none" stroke={TEXT_LIGHT} strokeWidth={1.5} />
      <text x={ox + aw / 2} y={oy + 16} textAnchor="middle" fontSize={11} fill={TEXT} fontWeight={600}>{a}</text>
      <text x={ox - 20} y={oy - bh / 2} textAnchor="middle" fontSize={11} fill={TEXT} fontWeight={600}>{b}</text>
      <text x={(ox + x1 + x2) / 3} y={(oy + y1 + y2) / 3 - 6} textAnchor="middle" fontSize={11} fill={TEXT} fontWeight={600}>c = {Math.hypot(a, b).toFixed(2)}</text>
      <text x={ox + 8} y={oy - 8} fontSize={9} fill={TEXT_LIGHT}>{angleDeg.toFixed(0)}°</text>
    </g>
  );
};

const UnitCircle = ({ spec }: { spec: DiagramSpec }) => {
  const angle = ((spec.angle ?? 45) * Math.PI) / 180;
  const cx = SVG_W / 2;
  const cy = SVG_H / 2;
  const r = Math.min(SVG_W, SVG_H) / 2 - 30;
  const x = cx + r * Math.cos(angle);
  const y = cy - r * Math.sin(angle);
  const px = x;
  const py = cy;
  return (
    <g>
      <line x1={cx - r - 20} y1={cy} x2={cx + r + 20} y2={cy} stroke={STROKE_2} strokeWidth={1} />
      <line x1={cx} y1={cy + r + 20} x2={cx} y2={cy - r - 20} stroke={STROKE_2} strokeWidth={1} />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={STROKE} strokeWidth={2} />
      <line x1={cx} y1={cy} x2={x} y2={y} stroke={STROKE} strokeWidth={2} />
      {spec.showCos && <line x1={x} y1={y} x2={px} y2={py} stroke="#10b981" strokeWidth={2.5} />}
      {spec.showSin && <line x1={px} y1={py} x2={x} y2={y} stroke="#ef4444" strokeWidth={2.5} />}
      {spec.showTan && (
        <line x1={px} y1={cy} x2={px} y2={cy - r * Math.tan(angle)} stroke="#f59e0b" strokeWidth={2.5} strokeDasharray="3 2" />
      )}
      <circle cx={x} cy={y} r={3} fill={STROKE} />
      <text x={cx + 12} y={cy - 6} fontSize={11} fill={TEXT} fontWeight={600}>1</text>
      <text x={cx + 12} y={cy - r + 4} fontSize={11} fill={TEXT} fontWeight={600}>cos θ</text>
      <text x={cx + r - 4} y={cy + 14} fontSize={11} fill={TEXT} fontWeight={600}>sin θ</text>
      <text x={cx + 4} y={cy + 14} fontSize={10} fill={TEXT_LIGHT}>0</text>
      <text x={cx + 8} y={cy - 4} fontSize={10} fill={TEXT_LIGHT}>θ</text>
      <text x={cx + 4} y={cy - 8} fontSize={10} fill={TEXT_LIGHT}>{spec.angle ?? 45}°</text>
    </g>
  );
};

const CoordinatePlane = ({ spec }: { spec: DiagramSpec }) => {
  const [xMin, xMax] = spec.xRange ?? [-5, 5];
  const [yMin, yMax] = spec.yRange ?? [-5, 5];
  const padX = 30;
  const padY = 20;
  const plotW = SVG_W - 2 * padX;
  const plotH = SVG_H - 2 * padY;
  const sx = (x: number) => padX + ((x - xMin) / (xMax - xMin)) * plotW;
  const sy = (y: number) => padY + ((yMax - y) / (yMax - yMin)) * plotH;
  const xStep = Math.max(1, Math.ceil((xMax - xMin) / 10));
  const yStep = Math.max(1, Math.ceil((yMax - yMin) / 10));
  return (
    <g>
      <rect x={padX} y={padY} width={plotW} height={plotH} fill="#fafafa" className="dark:fill-white/[0.02]" stroke={STROKE_2} strokeWidth={1} />
      {Array.from({ length: Math.floor((xMax - xMin) / xStep) + 1 }, (_, i) => {
        const xv = xMin + i * xStep;
        if (xv === 0) return null;
        return <line key={`gx${i}`} x1={sx(xv)} y1={padY} x2={sx(xv)} y2={padY + plotH} stroke={STROKE_2} strokeWidth={0.5} strokeDasharray="2 3" />;
      })}
      {Array.from({ length: Math.floor((yMax - yMin) / yStep) + 1 }, (_, i) => {
        const yv = yMin + i * yStep;
        if (yv === 0) return null;
        return <line key={`gy${i}`} x1={padX} y1={sy(yv)} x2={padX + plotW} y2={sy(yv)} stroke={STROKE_2} strokeWidth={0.5} strokeDasharray="2 3" />;
      })}
      <line x1={sx(0)} y1={padY} x2={sx(0)} y2={padY + plotH} stroke={TEXT_LIGHT} strokeWidth={1.5} />
      <line x1={padX} y1={sy(0)} x2={padX + plotW} y2={sy(0)} stroke={TEXT_LIGHT} strokeWidth={1.5} />
      {spec.line && (
        <line x1={sx(spec.line.x1)} y1={sy(spec.line.y1)} x2={sx(spec.line.x2)} y2={sy(spec.line.y2)} stroke={STROKE} strokeWidth={2.5} />
      )}
      {spec.line?.label && (
        <text x={sx(spec.line.x2) + 4} y={sy(spec.line.y2) - 4} fontSize={10} fill={STROKE} fontWeight={600}>{spec.line.label}</text>
      )}
      {(spec.points ?? []).map((p, i) => (
        <g key={i}>
          <circle cx={sx(p.x)} cy={sy(p.y)} r={3.5} fill={STROKE} />
          {p.label && <text x={sx(p.x) + 6} y={sy(p.y) - 6} fontSize={10} fill={TEXT} fontWeight={600}>{p.label}</text>}
        </g>
      ))}
    </g>
  );
};

const Triangle = ({ spec }: { spec: DiagramSpec }) => {
  const verts = spec.vertices ?? [{ x: 0, y: 0, label: 'A' }, { x: 4, y: 0, label: 'B' }, { x: 1, y: 3, label: 'C' }];
  const xs = verts.map(v => v.x);
  const ys = verts.map(v => v.y);
  const xMin = Math.min(...xs) - 1;
  const xMax = Math.max(...xs) + 1;
  const yMin = Math.min(...ys) - 1;
  const yMax = Math.max(...ys) + 1;
  const padX = 30;
  const padY = 30;
  const plotW = SVG_W - 2 * padX;
  const plotH = SVG_H - 2 * padY;
  const sx = (x: number) => padX + ((x - xMin) / (xMax - xMin)) * plotW;
  const sy = (y: number) => padY + ((yMax - y) / (yMax - yMin)) * plotH;
  return (
    <g>
      <polygon
        points={verts.map(v => `${sx(v.x)},${sy(v.y)}`).join(' ')}
        fill={FILL}
        fillOpacity={0.5}
        stroke={STROKE}
        strokeWidth={2.5}
        strokeLinejoin="round"
      />
      {verts.map((v, i) => (
        <g key={i}>
          <circle cx={sx(v.x)} cy={sy(v.y)} r={3.5} fill={STROKE} />
          <text x={sx(v.x) + (i === 0 ? -12 : i === 1 ? 8 : 4)} y={sy(v.y) + (i === 0 ? 14 : i === 1 ? 14 : -8)} fontSize={12} fill={TEXT} fontWeight={700}>{v.label}</text>
        </g>
      ))}
    </g>
  );
};

const CircleDiagram = ({ spec }: { spec: DiagramSpec }) => {
  const cx = SVG_W / 2;
  const cy = SVG_H / 2;
  const r = Math.min(SVG_W, SVG_H) / 2 - 30;
  return (
    <g>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={STROKE} strokeWidth={2.5} />
      <circle cx={cx} cy={cy} r={3} fill={TEXT_LIGHT} />
      <line x1={cx} y1={cy} x2={cx + r} y2={cy} stroke="#ef4444" strokeWidth={2} />
      <text x={cx + r / 2} y={cy - 6} textAnchor="middle" fontSize={11} fill="#ef4444" fontWeight={600}>r = {spec.radius ?? r.toFixed(1)}</text>
      <text x={cx + 4} y={cy + 12} fontSize={10} fill={TEXT_LIGHT}>O</text>
    </g>
  );
};

const NumberLine = ({ spec }: { spec: DiagramSpec }) => {
  const min = spec.min ?? 0;
  const max = spec.max ?? 10;
  const padX = 30;
  const lineY = SVG_H / 2;
  const lineW = SVG_W - 2 * padX;
  const sx = (x: number) => padX + ((x - min) / (max - min)) * lineW;
  const range = max - min;
  const tickStep = range <= 10 ? 1 : range <= 20 ? 2 : range <= 50 ? 5 : 10;
  return (
    <g>
      <line x1={padX} y1={lineY} x2={SVG_W - padX} y2={lineY} stroke={TEXT_LIGHT} strokeWidth={1.5} />
      <polygon points={`${padX - 6},${lineY - 4} ${padX - 6},${lineY + 4} ${padX},${lineY}`} fill={TEXT_LIGHT} />
      <polygon points={`${SVG_W - padX + 6},${lineY - 4} ${SVG_W - padX + 6},${lineY + 4} ${SVG_W - padX},${lineY}`} fill={TEXT_LIGHT} />
      {(spec.shade ?? []).map(([a, b], i) => (
        <line key={`s${i}`} x1={sx(a)} y1={lineY - 6} x2={sx(b)} y2={lineY - 6} stroke={STROKE} strokeWidth={6} strokeLinecap="round" />
      ))}
      {Array.from({ length: Math.floor(range / tickStep) + 1 }, (_, i) => {
        const xv = min + i * tickStep;
        return (
          <g key={`t${i}`}>
            <line x1={sx(xv)} y1={lineY - 4} x2={sx(xv)} y2={lineY + 4} stroke={TEXT_LIGHT} strokeWidth={1} />
            <text x={sx(xv)} y={lineY + 16} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT}>{xv}</text>
          </g>
        );
      })}
      {(spec.points ?? []).map((p, i) => (
        <g key={`p${i}`}>
          <circle cx={sx(p.x)} cy={lineY} r={5} fill={STROKE} />
          {p.label && <text x={sx(p.x)} y={lineY - 12} textAnchor="middle" fontSize={10} fill={TEXT} fontWeight={700}>{p.label}</text>}
        </g>
      ))}
    </g>
  );
};

const VectorDiagram = ({ spec }: { spec: DiagramSpec }) => {
  const cx = SVG_W / 2;
  const cy = SVG_H / 2;
  const scale = 40;
  const ex = cx + (spec.end?.x ?? 3) * scale;
  const ey = cy - (spec.end?.y ?? 4) * scale;
  return (
    <g>
      <line x1={20} y1={cy} x2={SVG_W - 20} y2={cy} stroke={STROKE_2} strokeWidth={1} />
      <line x1={cx} y1={SVG_H - 20} x2={cx} y2={20} stroke={STROKE_2} strokeWidth={1} />
      <line x1={cx} y1={cy} x2={ex} y2={ey} stroke={STROKE} strokeWidth={2.5} />
      <polygon
        points={`${ex},${ey} ${ex - 8 * Math.cos(Math.atan2(cy - ey, ex - cx) - 0.4)},${ey - 8 * Math.sin(Math.atan2(cy - ey, ex - cx) - 0.4)} ${ex - 8 * Math.cos(Math.atan2(cy - ey, ex - cx) + 0.4)},${ey - 8 * Math.sin(Math.atan2(cy - ey, ex - cx) + 0.4)}`}
        fill={STROKE}
      />
      <circle cx={cx} cy={cy} r={3} fill={TEXT_LIGHT} />
      {spec.label && <text x={ex + 8} y={ey - 4} fontSize={11} fill={TEXT} fontWeight={700}>{spec.label}</text>}
    </g>
  );
};

const BoxPlot = ({ spec }: { spec: DiagramSpec }) => {
  const min = spec.min ?? 0;
  const max = spec.max ?? 10;
  const q1 = spec.q1 ?? 3;
  const med = spec.median ?? 5;
  const q3 = spec.q3 ?? 7;
  const padX = 50;
  const lineY = SVG_H / 2;
  const lineW = SVG_W - 2 * padX;
  const sx = (x: number) => padX + ((x - min) / (max - min)) * lineW;
  return (
    <g>
      <line x1={padX} y1={lineY} x2={SVG_W - padX} y2={lineY} stroke={TEXT_LIGHT} strokeWidth={1.5} />
      <line x1={padX} y1={lineY} x2={padX} y2={lineY} stroke={TEXT_LIGHT} strokeWidth={1.5} />
      <line x1={sx(min)} y1={lineY - 18} x2={sx(min)} y2={lineY + 18} stroke={TEXT_LIGHT} strokeWidth={1.5} />
      <line x1={sx(max)} y1={lineY - 18} x2={sx(max)} y2={lineY + 18} stroke={TEXT_LIGHT} strokeWidth={1.5} />
      <line x1={sx(min)} y1={lineY} x2={sx(q1)} y2={lineY} stroke={STROKE} strokeWidth={2} />
      <line x1={sx(q3)} y1={lineY} x2={sx(max)} y2={lineY} stroke={STROKE} strokeWidth={2} />
      <rect x={sx(q1)} y={lineY - 30} width={sx(q3) - sx(q1)} height={60} fill={FILL} stroke={STROKE} strokeWidth={2} />
      <line x1={sx(med)} y1={lineY - 30} x2={sx(med)} y2={lineY + 30} stroke="#ef4444" strokeWidth={3} />
      <text x={sx(min)} y={lineY + 36} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT}>min {min}</text>
      <text x={sx(q1)} y={lineY + 36} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT}>Q1 {q1}</text>
      <text x={sx(med)} y={lineY + 36} textAnchor="middle" fontSize={9} fill="#ef4444" fontWeight={700}>med {med}</text>
      <text x={sx(q3)} y={lineY + 36} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT}>Q3 {q3}</text>
      <text x={sx(max)} y={lineY + 36} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT}>max {max}</text>
    </g>
  );
};

const BarChartDiagram = ({ spec }: { spec: DiagramSpec }) => {
  const bars = spec.bars ?? [{ label: 'A', value: 5 }, { label: 'B', value: 8 }];
  const maxV = Math.max(...bars.map(b => b.value));
  const padX = 30;
  const padY = 30;
  const plotW = SVG_W - 2 * padX;
  const plotH = SVG_H - 2 * padY;
  const barW = (plotW / bars.length) * 0.7;
  const gap = (plotW / bars.length) * 0.3;
  return (
    <g>
      <line x1={padX} y1={padY + plotH} x2={SVG_W - padX} y2={padY + plotH} stroke={TEXT_LIGHT} strokeWidth={1.5} />
      <line x1={padX} y1={padY} x2={padX} y2={padY + plotH} stroke={TEXT_LIGHT} strokeWidth={1.5} />
      {bars.map((b, i) => {
        const h = (b.value / maxV) * plotH;
        const x = padX + i * (barW + gap) + gap / 2;
        const y = padY + plotH - h;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={h} fill={b.color ?? STROKE} />
            <text x={x + barW / 2} y={y - 4} textAnchor="middle" fontSize={10} fill={TEXT} fontWeight={700}>{b.value}</text>
            <text x={x + barW / 2} y={padY + plotH + 14} textAnchor="middle" fontSize={10} fill={TEXT_LIGHT}>{b.label}</text>
          </g>
        );
      })}
    </g>
  );
};

const NormalDistribution = ({ spec }: { spec: DiagramSpec }) => {
  const mean = spec.mean ?? 0;
  const std = spec.stdDev ?? 1;
  const [xMin, xMax] = spec.range ?? [mean - 4 * std, mean + 4 * std];
  const padX = 30;
  const padY = 30;
  const plotW = SVG_W - 2 * padX;
  const plotH = SVG_H - 2 * padY;
  const sx = (x: number) => padX + ((x - xMin) / (xMax - xMin)) * plotW;
  const peak = 1 / (std * Math.sqrt(2 * Math.PI));
  const maxY = peak;
  const sy = (y: number) => padY + plotH - (y / maxY) * plotH * 0.85;

  const points: string[] = [];
  const N = 100;
  for (let i = 0; i <= N; i++) {
    const x = xMin + (i / N) * (xMax - xMin);
    const y = (1 / (std * Math.sqrt(2 * Math.PI))) * Math.exp(-((x - mean) ** 2) / (2 * std * std));
    points.push(`${sx(x)},${sy(y)}`);
  }

  const pathData = `M ${sx(xMin)},${sy(0)} L ${points.join(' L ')} L ${sx(xMax)},${sy(0)} Z`;

  return (
    <g>
      <line x1={padX} y1={padY + plotH} x2={SVG_W - padX} y2={padY + plotH} stroke={TEXT_LIGHT} strokeWidth={1.5} />
      <path d={pathData} fill={FILL} stroke={STROKE} strokeWidth={2} />
      {spec.shadeRange && (
        <ShadedRegion
          xMin={xMin}
          xMax={xMax}
          shadeFrom={spec.shadeRange.from}
          shadeTo={spec.shadeRange.to}
          mean={mean}
          std={std}
          maxY={maxY}
          plotH={plotH}
          padX={padX}
          padY={padY}
          plotW={plotW}
        />
      )}
      <line x1={sx(mean)} y1={padY + plotH} x2={sx(mean)} y2={sy(maxY) + 4} stroke={TEXT_LIGHT} strokeWidth={1.5} strokeDasharray="2 2" />
      <text x={sx(mean)} y={padY + plotH + 14} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT}>μ={mean}</text>
      <text x={sx(mean + std)} y={padY + plotH + 28} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT}>σ={std}</text>
    </g>
  );
};

const ShadedRegion = ({ xMin, xMax, shadeFrom, shadeTo, mean, std, maxY, plotH, padX, padY, plotW }: {
  xMin: number; xMax: number; shadeFrom: number; shadeTo: number; mean: number; std: number; maxY: number; plotH: number; padX: number; padY: number; plotW: number;
}) => {
  const sx = (x: number) => padX + ((x - xMin) / (xMax - xMin)) * plotW;
  const sy = (y: number) => padY + plotH - (y / maxY) * plotH * 0.85;
  const points: string[] = [];
  const N = 50;
  const a = Math.max(xMin, shadeFrom);
  const b = Math.min(xMax, shadeTo);
  for (let i = 0; i <= N; i++) {
    const x = a + (i / N) * (b - a);
    const y = (1 / (std * Math.sqrt(2 * Math.PI))) * Math.exp(-((x - mean) ** 2) / (2 * std * std));
    points.push(`${sx(x)},${sy(y)}`);
  }
  if (points.length < 2) return null;
  const path = `M ${sx(a)},${sy(0)} L ${points.join(' L ')} L ${sx(b)},${sy(0)} Z`;
  return <path d={path} fill={STROKE} fillOpacity={0.4} />;
};

// === BEARING DIAGRAM ===
// A point P with a North arrow and a line at the given bearing (degrees clockwise from North).
// Distance labels the line. Standard 3-digit bearing notation: 045°, 120°, etc.
const BearingDiagram = ({ spec }: { spec: DiagramSpec }) => {
  const bearing = spec.bearing ?? 45;
  const dist = spec.distance ?? 5;
  const from = spec.fromPoint ?? { x: 0, y: 0, label: 'P' };
  const to = spec.toPoint;
  // convert bearing to math angle: 0° = up (North), clockwise positive
  const rad = (bearing * Math.PI) / 180;
  const len = 110;
  const cx = 200;
  const cy = 150;
  const endX = cx + len * Math.sin(rad);
  const endY = cy - len * Math.cos(rad);
  const a1 = rad - Math.PI / 2;
  const a2 = a1 + Math.PI;
  const arcR = 28;
  const arcEndX = cx + arcR * Math.sin(a1);
  const arcEndY = cy - arcR * Math.cos(a1);
  const arcStartX = cx + arcR * Math.sin(a2);
  const arcStartY = cy - arcR * Math.cos(a2);
  const largeArc = bearing > 180 ? 1 : 0;
  return (
    <g>
      <text x={cx} y={18} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT} fontWeight={700}>N</text>
      <line x1={cx} y1={28} x2={cx} y2={48} stroke={TEXT_LIGHT} strokeWidth={1.2} markerEnd="url(#arrowNorth)" />
      <text x={cx + 12} y={40} fontSize={8} fill={TEXT_LIGHT}>↑</text>
      <line x1={cx} y1={cy} x2={endX} y2={endY} stroke={STROKE} strokeWidth={2} markerEnd="url(#arrowBearing)" />
      <path d={`M ${arcStartX},${arcStartY} A ${arcR},${arcR} 0 ${largeArc} 1 ${arcEndX},${arcEndY}`} fill="none" stroke="#ef4444" strokeWidth={1.2} />
      <text x={cx + 38 * Math.sin(rad / 2) + 10} y={cy - 38 * Math.cos(rad / 2) + 4} fontSize={9} fill="#ef4444" fontWeight={700}>{String(bearing).padStart(3, '0')}°</text>
      <text x={(cx + endX) / 2 + 8 * Math.cos(rad)} y={(cy + endY) / 2 - 8 * Math.sin(rad)} fontSize={8} fill={STROKE} fontWeight={600}>{dist} km</text>
      <circle cx={cx} cy={cy} r={3} fill={STROKE} />
      <text x={cx - 6} y={cy + 4} fontSize={9} fill={TEXT} fontWeight={700}>{from.label || 'P'}</text>
      {to && (
        <>
          <circle cx={endX} cy={endY} r={3} fill={STROKE} />
          <text x={endX + 6} y={endY + 4} fontSize={9} fill={TEXT} fontWeight={700}>{to.label || 'Q'}</text>
        </>
      )}
      <defs>
        <marker id="arrowNorth" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={TEXT_LIGHT} />
        </marker>
        <marker id="arrowBearing" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={STROKE} />
        </marker>
      </defs>
    </g>
  );
};

// === LOCUS DIAGRAM ===
// Shows the region/set of points satisfying a geometric condition.
// Types: from-point (circle), from-line (parallel band), two-points (ellipse / intersection),
// intersect (overlap of two regions), region (custom polygon), path (custom).
const LocusDiagram = ({ spec }: { spec: DiagramSpec }) => {
  const ltype = spec.locusType ?? 'from-point';
  const cx = 200;
  const cy = 150;
  if (ltype === 'from-point' && spec.locusRadius != null) {
    const r = spec.locusRadius * 25;
    return (
      <g>
        <circle cx={cx} cy={cy} r={3} fill={STROKE} />
        <text x={cx + 6} y={cy - 6} fontSize={9} fill={TEXT} fontWeight={700}>{spec.fromPoint?.label || 'A'}</text>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={STROKE} strokeWidth={1.5} strokeDasharray="4 3" />
        <circle cx={cx} cy={cy} r={r} fill={STROKE} fillOpacity={0.15} />
        <text x={cx} y={cy + 5} textAnchor="middle" fontSize={9} fill={STROKE} fontWeight={700}>r = {spec.locusRadius}</text>
        <text x={SVG_W / 2} y={SVG_H - 12} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT}>Locus: points within {spec.locusRadius} units of A</text>
      </g>
    );
  }
  if (ltype === 'from-line' && spec.locusLine && spec.locusRadius != null) {
    const { p1, p2 } = spec.locusLine;
    const pad = 30;
    const dx = p2.x - p1.x, dy = p2.y - p1.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    const ux = -dy / len, uy = dx / len;
    const r = spec.locusRadius * 20;
    const o1 = { x: p1.x + ux * r, y: p1.y + uy * r };
    const o2 = { x: p2.x + ux * r, y: p2.y + uy * r };
    const o3 = { x: p2.x - ux * r, y: p2.y - uy * r };
    const o4 = { x: p1.x - ux * r, y: p1.y - uy * r };
    return (
      <g>
        <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke={STROKE} strokeWidth={1.5} />
        <text x={(p1.x + p2.x) / 2} y={(p1.y + p2.y) / 2 - 6} textAnchor="middle" fontSize={9} fill={STROKE} fontWeight={700}>{spec.locusLine.label || 'L'}</text>
        <polygon points={`${o1.x},${o1.y} ${o2.x},${o2.y} ${o3.x},${o3.y} ${o4.x},${o4.y}`} fill={STROKE} fillOpacity={0.15} stroke={STROKE} strokeWidth={1} strokeDasharray="3 3" />
        <text x={SVG_W / 2} y={SVG_H - 12} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT}>Locus: points within {spec.locusRadius} of line {spec.locusLine.label || 'L'}</text>
      </g>
    );
  }
  if (ltype === 'intersect' && spec.locusRadius != null && spec.locusRadius2 != null) {
    const r1 = spec.locusRadius * 25;
    const r2 = spec.locusRadius2 * 25;
    const c1 = spec.fromPoint ?? { x: 140, y: 150 };
    const c2 = spec.toPoint ?? { x: 260, y: 150 };
    return (
      <g>
        <circle cx={c1.x} cy={c1.y} r={r1} fill={STROKE} fillOpacity={0.1} stroke={STROKE} strokeWidth={1.5} strokeDasharray="4 3" />
        <circle cx={c2.x} cy={c2.y} r={r2} fill="#ef4444" fillOpacity={0.1} stroke="#ef4444" strokeWidth={1.5} strokeDasharray="4 3" />
        <circle cx={c1.x} cy={c1.y} r={3} fill={STROKE} />
        <circle cx={c2.x} cy={c2.y} r={3} fill="#ef4444" />
        <text x={c1.x + 6} y={c1.y - 6} fontSize={9} fill={STROKE} fontWeight={700}>{c1.label || 'A'}</text>
        <text x={c2.x + 6} y={c2.y - 6} fontSize={9} fill="#ef4444" fontWeight={700}>{c2.label || 'B'}</text>
        <text x={SVG_W / 2} y={SVG_H - 12} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT}>Intersection: points within {spec.locusRadius} of {c1.label || 'A'} AND {spec.locusRadius2} of {c2.label || 'B'}</text>
      </g>
    );
  }
  if (ltype === 'path' && spec.locusPath && spec.locusPath.length >= 2) {
    const pathStr = 'M ' + spec.locusPath.map(p => `${p.x},${p.y}`).join(' L ') + ' Z';
    return (
      <g>
        <polygon points={spec.locusPath.map(p => `${p.x},${p.y}`).join(' ')} fill={STROKE} fillOpacity={0.2} stroke={STROKE} strokeWidth={1.5} strokeDasharray="4 3" />
        <text x={SVG_W / 2} y={SVG_H - 12} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT}>Locus path</text>
      </g>
    );
  }
  return <text x={SVG_W / 2} y={SVG_H / 2} textAnchor="middle" fontSize={10} fill={TEXT_LIGHT}>Locus: specify type and parameters</text>;
};

// === SCALE DRAWING ===
// Renders a diagram to scale with a scale bar showing the real-world distance.
const ScaleDrawing = ({ spec }: { spec: DiagramSpec }) => {
  const scaleFactor = spec.scale ?? 1;
  const unit = spec.scaleUnit ?? 'm';
  const from = spec.fromPoint ?? { x: 60, y: 220, label: 'A' };
  const to = spec.toPoint ?? { x: 340, y: 220, label: 'B' };
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const pixelDist = Math.sqrt(dx * dx + dy * dy);
  const realDist = pixelDist / scaleFactor;
  const barLen = 80;
  const barReal = barLen / scaleFactor;
  const barX = SVG_W - barLen - 30;
  const barY = SVG_H - 20;
  return (
    <g>
      {spec.vertices?.map((v, i) => {
        if (i === 0) return null;
        const prev = spec.vertices![i - 1];
        return <line key={i} x1={prev.x} y1={prev.y} x2={v.x} y2={v.y} stroke={STROKE} strokeWidth={2} />;
      })}
      {spec.vertices?.map((v, i) => (
        <g key={`v${i}`}>
          <circle cx={v.x} cy={v.y} r={3} fill={STROKE} />
          {v.label && <text x={v.x + 6} y={v.y - 6} fontSize={9} fill={TEXT} fontWeight={700}>{v.label}</text>}
        </g>
      ))}
      <line x1={barX} y1={barY} x2={barX + barLen} y2={barY} stroke={TEXT} strokeWidth={2} />
      <line x1={barX} y1={barY - 4} x2={barX} y2={barY + 4} stroke={TEXT} strokeWidth={2} />
      <line x1={barX + barLen / 2} y1={barY - 3} x2={barX + barLen / 2} y2={barY + 3} stroke={TEXT} strokeWidth={1.5} />
      <line x1={barX + barLen} y1={barY - 4} x2={barX + barLen} y2={barY + 4} stroke={TEXT} strokeWidth={2} />
      <text x={barX + barLen / 2} y={barY + 14} textAnchor="middle" fontSize={8} fill={TEXT_LIGHT}>{barReal.toFixed(1)} {unit}</text>
      <text x={barX + barLen + 8} y={barY + 3} fontSize={8} fill={TEXT_LIGHT}>1:{Math.round(scaleFactor)}</text>
      <text x={SVG_W / 2} y={SVG_H - 38} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT}>Real distance: {realDist.toFixed(2)} {unit}</text>
    </g>
  );
};

// === BEARING JOURNEY ===
// Multi-leg bearing path: origin → Q1 → Q2 → ... with bearing arcs, north arrows, distances, scale bar.
// Automatically computes pixel positions from real bearings + distances.
const BearingJourney = ({ spec }: { spec: DiagramSpec }) => {
  const j = spec.bearingJourney;
  if (!j || j.legs.length === 0) return <text x={SVG_W / 2} y={SVG_H / 2} textAnchor="middle" fontSize={10} fill={TEXT_LIGHT}>No journey data</text>;

  const PX_PER_KM = 7;
  const padX = 50;
  const padY = 50;

  // compute world coords (km, y-up) then convert to pixels
  const worldPts: { x: number; y: number; label: string }[] = [{ x: 0, y: 0, label: j.origin?.label || 'Start' }];
  let wx = 0, wy = 0;
  for (let i = 0; i < j.legs.length; i++) {
    const leg = j.legs[i];
    const rad = (leg.bearing * Math.PI) / 180;
    wx += leg.distance * Math.sin(rad);
    wy += leg.distance * Math.cos(rad);
    const fallbackLabel = String.fromCharCode(65 + i + 1); // B, C, D...
    worldPts.push({ x: wx, y: wy, label: leg.toLabel || (leg as any).label || fallbackLabel });
  }

  // bounds
  const xs = worldPts.map(p => p.x), ys = worldPts.map(p => p.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const rangeX = maxX - minX || 1, rangeY = maxY - minY || 1;
  const drawW = SVG_W - 2 * padX, drawH = SVG_H - 2 * padY;
  const fitScale = Math.min(drawW / rangeX, drawH / rangeY);
  const sc = Math.min(fitScale, PX_PER_KM);
  const offsetX = padX + (drawW - rangeX * sc) / 2 - minX * sc;
  const offsetY = padY + (drawH - rangeY * sc) / 2 + maxY * sc;
  const px = (wx2: number) => offsetX + wx2 * sc;
  const py = (wy2: number) => offsetY - wy2 * sc;

  const pts = worldPts.map(p => ({ x: px(p.x), y: py(p.y), label: p.label }));

  // compute real straight-line distance from origin to final point
  const realDist = Math.sqrt(wx * wx + wy * wy);
  const realBearing = ((Math.atan2(wx, wy) * 180) / Math.PI + 360) % 360;

  // scale bar
  const barKm = realDist > 20 ? 10 : realDist > 8 ? 5 : 2;
  const barPx = barKm * sc;
  const barX = SVG_W - barPx - 20;
  const barY = SVG_H - 14;

  // arc helper: small arc at a waypoint showing bearing from North
  const bearingArc = (cx: number, cy: number, bearing: number, r = 18) => {
    const startA = 0; // North
    const endA = bearing;
    const largeArc = bearing > 180 ? 1 : 0;
    const sx2 = cx + r * Math.sin(startA * Math.PI / 180);
    const sy2 = cy - r * Math.cos(startA * Math.PI / 180);
    const ex = cx + r * Math.sin(endA * Math.PI / 180);
    const ey = cy - r * Math.cos(endA * Math.PI / 180);
    return `M ${sx2},${sy2} A ${r},${r} 0 ${largeArc} 1 ${ex},${ey}`;
  };

  return (
    <g>
      {pts.map((p, i) => {
        if (i === 0) return null;
        const prev = pts[i - 1];
        const leg = j.legs[i - 1];
        const dx2 = p.x - prev.x, dy2 = p.y - prev.y;
        const len = Math.sqrt(dx2 * dx2 + dy2 * dy2);
        if (len < 1) return null;
        const ux = dx2 / len, uy = dy2 / len;
        const midX = (prev.x + p.x) / 2;
        const midY = (prev.y + p.y) / 2;
        const labelOff = 12;
        const normX = -uy, normY = ux;
        const lx = midX + normX * labelOff;
        const ly = midY - normY * labelOff;
        return (
          <g key={`leg${i}`}>
            <line x1={prev.x} y1={prev.y} x2={p.x - ux * 8} y2={p.y - uy * 8} stroke={STROKE} strokeWidth={2} />
            <polygon points={`${p.x},${p.y} ${(p.x - ux * 8) + normX * 4},${(p.y - uy * 8) - normY * 4} ${(p.x - ux * 8) - normX * 4},${(p.y - uy * 8) + normY * 4}`} fill={STROKE} />
            <text x={lx} y={ly} textAnchor="middle" fontSize={8} fill={STROKE} fontWeight={600}>{leg.distance} km</text>
          </g>
        );
      })}

      {pts.map((p, i) => {
        const legBearing = i === 0 ? j.legs[0].bearing : j.legs[i - 1].bearing;
        const arcPath = bearingArc(p.x, p.y, legBearing);
        const labelAngle = legBearing / 2;
        const labelR = 26;
        const lx2 = p.x + labelR * Math.sin(labelAngle * Math.PI / 180);
        const ly2 = p.y - labelR * Math.cos(labelAngle * Math.PI / 180);
        return (
          <g key={`wp${i}`}>
            <circle cx={p.x} cy={p.y} r={3.5} fill={STROKE} />
            <text x={p.x - 10} y={p.y + 12} fontSize={10} fill={TEXT} fontWeight={800}>{p.label}</text>
            {i === 0 && (
              <g>
                <line x1={p.x} y1={p.y - 24} x2={p.x} y2={p.y - 38} stroke={TEXT_LIGHT} strokeWidth={1} />
                <polygon points={`${p.x},${p.y - 38} ${p.x - 3},${p.y - 32} ${p.x + 3},${p.y - 32}`} fill={TEXT_LIGHT} />
                <text x={p.x} y={p.y - 42} textAnchor="middle" fontSize={7} fill={TEXT_LIGHT} fontWeight={700}>N</text>
              </g>
            )}
            {i > 0 && (
              <g>
                <line x1={p.x} y1={p.y - 18} x2={p.x} y2={p.y - 28} stroke={TEXT_LIGHT} strokeWidth={0.8} />
                <polygon points={`${p.x},${p.y - 28} ${p.x - 2},${p.y - 23} ${p.x + 2},${p.y - 23}`} fill={TEXT_LIGHT} />
                <text x={p.x} y={p.y - 31} textAnchor="middle" fontSize={6} fill={TEXT_LIGHT} fontWeight={700}>N</text>
                <path d={arcPath} fill="none" stroke="#ef4444" strokeWidth={1.2} />
                <text x={lx2} y={ly2} textAnchor="middle" fontSize={7} fill="#ef4444" fontWeight={700}>{String(legBearing).padStart(3, '0')}°</text>
              </g>
            )}
          </g>
        );
      })}

      {/* dashed return line from last point back to origin */}
      <line x1={pts[pts.length - 1].x} y1={pts[pts.length - 1].y} x2={pts[0].x} y2={pts[0].y} stroke={TEXT_LIGHT} strokeWidth={1} strokeDasharray="4 3" />
      <text x={(pts[0].x + pts[pts.length - 1].x) / 2 + 12} y={(pts[0].y + pts[pts.length - 1].y) / 2 - 8} fontSize={7} fill={TEXT_LIGHT} fontWeight={600}>{realDist.toFixed(1)} km ({String(Math.round(realBearing)).padStart(3, '0')}°)</text>

      {/* scale bar */}
      <line x1={barX} y1={barY} x2={barX + barPx} y2={barY} stroke={TEXT} strokeWidth={2} />
      <line x1={barX} y1={barY - 4} x2={barX} y2={barY + 4} stroke={TEXT} strokeWidth={2} />
      <line x1={barX + barPx} y1={barY - 4} x2={barX + barPx} y2={barY + 4} stroke={TEXT} strokeWidth={2} />
      <text x={barX + barPx / 2} y={barY + 12} textAnchor="middle" fontSize={7} fill={TEXT_LIGHT}>{barKm} km</text>
      <text x={barX + barPx + 4} y={barY + 3} fontSize={6} fill={TEXT_LIGHT}>1:{Math.round(sc)}</text>
    </g>
  );
};

// === BEARING + ELEVATION (3D TRIG) ===
// Top-down view: horizontal bearing from observer to target.
// Side view: elevation angle and true (slant) distance.
const BearingElevation = ({ spec }: { spec: DiagramSpec }) => {
  const bearing = spec.bearing ?? 60;
  const elev = spec.elevation ?? 30;
  const trueDist = spec.trueDistance ?? 10;
  const horizDist = trueDist * Math.cos((elev * Math.PI) / 180);
  const vertDist = trueDist * Math.sin((elev * Math.PI) / 180);
  const cx1 = 100;
  const cy1 = 150;
  const rad = (bearing * Math.PI) / 180;
  const r2 = 80;
  const targetX = cx1 + r2 * Math.sin(rad);
  const targetY = cy1 - r2 * Math.cos(rad);
  const arcR = 24;
  const a1 = rad - Math.PI / 2;
  const a2 = a1 + Math.PI;
  const arcStartX = cx1 + arcR * Math.sin(a2);
  const arcStartY = cy1 - arcR * Math.cos(a2);
  const arcEndX = cx1 + arcR * Math.sin(a1);
  const arcEndY = cy1 - arcR * Math.cos(a1);
  const largeArc = bearing > 180 ? 1 : 0;
  return (
    <g>
      <text x={SVG_W / 2} y={16} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT} fontWeight={700} letterSpacing={1}>3D BEARING + ELEVATION</text>
      <line x1={40} y1={cy1} x2={cx1 - 8} y2={cy1} stroke={TEXT_LIGHT} strokeWidth={1} strokeDasharray="2 2" />
      <line x1={cx1} y1={cy1 - 50} x2={cx1} y2={cy1} stroke={TEXT_LIGHT} strokeWidth={1.2} markerEnd="url(#bevN)" />
      <text x={cx1} y={cy1 - 55} textAnchor="middle" fontSize={8} fill={TEXT_LIGHT} fontWeight={700}>N</text>
      <line x1={cx1} y1={cy1} x2={targetX} y2={targetY} stroke={STROKE} strokeWidth={2} markerEnd="url(#bevB)" />
      <path d={`M ${arcStartX},${arcStartY} A ${arcR},${arcR} 0 ${largeArc} 1 ${arcEndX},${arcEndY}`} fill="none" stroke="#ef4444" strokeWidth={1.2} />
      <text x={cx1 + 30 * Math.sin(rad / 2) + 8} y={cy1 - 30 * Math.cos(rad / 2) + 4} fontSize={8} fill="#ef4444" fontWeight={700}>Bearing {String(bearing).padStart(3, '0')}°</text>
      <text x={targetX + 6} y={targetY} fontSize={8} fill={TEXT} fontWeight={700}>T</text>
      <circle cx={cx1} cy={cy1} r={3} fill={STROKE} />
      <text x={cx1 - 6} y={cy1 + 4} fontSize={8} fill={TEXT} fontWeight={700}>O</text>
      <text x={(cx1 + targetX) / 2 + 10} y={(cy1 + targetY) / 2 - 6} fontSize={8} fill={STROKE} fontWeight={600}>d = {horizDist.toFixed(1)}</text>
      <line x1={SVG_W - 130} y1={cy1 - 50} x2={SVG_W - 130} y2={cy1 + 50} stroke={TEXT_LIGHT} strokeWidth={1} strokeDasharray="2 2" />
      <line x1={SVG_W - 130} y1={cy1} x2={SVG_W - 30} y2={cy1} stroke={STROKE} strokeWidth={2} />
      <line x1={SVG_W - 130} y1={cy1} x2={SVG_W - 30} y2={cy1 - 60} stroke="#ef4444" strokeWidth={2.2} />
      <path d={`M ${SVG_W - 130 + 30},${cy1} A 30,30 0 0 0 ${SVG_W - 130 + 30 * Math.cos((elev * Math.PI) / 180)},${cy1 - 30 * Math.sin((elev * Math.PI) / 180)}`} fill="none" stroke="#ef4444" strokeWidth={1.2} />
      <text x={SVG_W - 130 + 36} y={cy1 - 8} fontSize={8} fill="#ef4444" fontWeight={700}>θ = {elev}°</text>
      <text x={SVG_W - 130 + 70} y={cy1 - 36} fontSize={8} fill="#ef4444" fontWeight={600}>L = {trueDist}</text>
      <text x={SVG_W - 130 + 50} y={cy1 + 12} fontSize={8} fill={STROKE} fontWeight={600}>h = {vertDist.toFixed(1)}</text>
      <defs>
        <marker id="bevN" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={TEXT_LIGHT} />
        </marker>
        <marker id="bevB" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={STROKE} />
        </marker>
      </defs>
    </g>
  );
};

const Shape3DWrapper = ({ children, caption, cameraPos = [3, 3, 3] }: { children: React.ReactNode; caption?: string; cameraPos?: [number, number, number] }) => {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (ref.current) {
      const r = ref.current.getBoundingClientRect();
      console.log('[3D] Container measured:', r.width, 'x', r.height);
    }
  });
  return (
    <div className="my-3 bg-white dark:bg-[#0a1024] border border-gray-200 dark:border-white/10 p-3">
      <div ref={ref} className="h-56 w-full min-w-[280px]">
        <Canvas
          camera={{ position: cameraPos, fov: 50 }}
          dpr={[1, 2]}
          style={{ width: '100%', height: '100%' }}
          onCreated={({ gl, scene, camera }) => {
            console.log('[3D] Canvas created. dom:', gl.domElement.width, 'x', gl.domElement.height, 'scene:', scene.children.length, 'cam:', [...camera.position.toArray()].map(v => v.toFixed(1)).join(','));
            gl.setClearColor(0x0a1024, 1);
          }}
        >
          <ambientLight intensity={0.7} />
          <directionalLight position={[5, 5, 5]} intensity={1.2} />
          <directionalLight position={[-5, 3, -2]} intensity={0.6} />
          {children}
          <OrbitControls enablePan={false} />
        </Canvas>
      </div>
      {caption && (
        <p className="text-[10px] text-center font-bold uppercase tracking-widest text-slate-500 dark:text-slate-500 mt-2">
          {caption}
        </p>
      )}
    </div>
  );
};

const Shape3DCube = ({ size }: { size: number }) => (
  <Shape3DWrapper caption={`Cube (side ${size})`}>
    <mesh>
      <boxGeometry args={[size, size, size]} />
      <meshStandardMaterial color="#6366f1" />
    </mesh>
    <mesh>
      <boxGeometry args={[size, size, size]} />
      <meshBasicMaterial color="#a5b4fc" wireframe />
    </mesh>
  </Shape3DWrapper>
);

const Shape3DSphere = ({ radius }: { radius: number }) => (
  <Shape3DWrapper caption={`Sphere (r = ${radius})`}>
    <mesh>
      <sphereGeometry args={[radius, 32, 32]} />
      <meshStandardMaterial color="#10b981" />
    </mesh>
  </Shape3DWrapper>
);

const Shape3DCylinder = ({ radius, height }: { radius: number; height: number }) => (
  <Shape3DWrapper caption={`Cylinder (r = ${radius}, h = ${height})`}>
    <mesh>
      <cylinderGeometry args={[radius, radius, height, 32]} />
      <meshStandardMaterial color="#f59e0b" />
    </mesh>
  </Shape3DWrapper>
);

const Shape3DCone = ({ radius, height }: { radius: number; height: number }) => (
  <Shape3DWrapper caption={`Cone (r = ${radius}, h = ${height})`}>
    <mesh position={[0, -height / 2, 0]}>
      <coneGeometry args={[radius, height, 32]} />
      <meshStandardMaterial color="#ef4444" />
    </mesh>
  </Shape3DWrapper>
);

const Shape3DBox = ({ w, d, h }: { w: number; d: number; h: number }) => (
  <Shape3DWrapper caption={`Rectangular prism (${w} × ${d} × ${h})`}>
    <mesh>
      <boxGeometry args={[w, h, d]} />
      <meshStandardMaterial color="#8b5cf6" transparent opacity={0.85} />
    </mesh>
  </Shape3DWrapper>
);

const useDarkMode = () => {
  const [isDark, setIsDark] = useState(false);
  useEffect(() => {
    const check = () => setIsDark(document.documentElement.classList.contains('dark'));
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => { obs.disconnect(); };
  }, []);
  return isDark;
};

const InteractiveUnitCircle = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isDark = useDarkMode();
  useEffect(() => {
    console.log('[JXG] InteractiveUnitCircle mount. JXG global:', typeof window !== 'undefined' ? !!(window as unknown as { JXG?: unknown }).JXG : 'SSR', 'JXG import:', typeof JXG);
    if (!containerRef.current) {
      console.warn('[JXG] No container ref!');
      return;
    }
    const r = containerRef.current.getBoundingClientRect();
    console.log('[JXG] Container size:', r.width, 'x', r.height);
    try {
      const board = JXG.JSXGraph.initBoard(containerRef.current, {
        boundingbox: [-1.4, 1.4, 1.4, -1.4],
        axis: true,
        showCopyright: false,
        showNavigation: false,
        pan: { needShift: true },
        zoom: { wheel: false, needShift: true }
      });
      console.log('[JXG] Board created. Children:', board.objectsList.length);
      const baseColor = isDark ? '#e2e8f0' : '#0f172a';
      board.create('axis', [[0, 0], [1, 0]], { strokeColor: baseColor, tickColor: baseColor, labelColor: baseColor });
      board.create('axis', [[0, 0], [0, 1]], { strokeColor: baseColor, tickColor: baseColor, labelColor: baseColor });
      board.create('circle', [[0, 0], [1, 0]], { strokeColor: '#6366f1', strokeWidth: 2, fillColor: '#6366f1', fillOpacity: 0.15 });
      const p = board.create('point', [Math.cos(Math.PI / 4), Math.sin(Math.PI / 4)], { name: 'P', color: '#ef4444', size: 4 });
      p.setAttribute({ fixed: false });
      const projX = board.create('point', [() => p.X(), 0], { color: '#10b981', size: 3, name: 'cos' });
      const projY = board.create('point', [0, () => p.Y()], { color: '#3b82f6', size: 3, name: 'sin' });
      board.create('segment', [p, projX], { strokeColor: '#10b981', strokeWidth: 2, dash: 2 });
      board.create('segment', [p, projY], { strokeColor: '#3b82f6', strokeWidth: 2, dash: 2 });
      board.create('segment', [[0, 0], p], { strokeColor: '#6366f1', strokeWidth: 2 });
      board.create('text', [1.0, 1.25, () => {
        const angle = (Math.atan2(p.Y(), p.X()) * 180 / Math.PI + 360) % 360;
        return `θ = ${angle.toFixed(0)}°    sin = ${p.Y().toFixed(2)}    cos = ${p.X().toFixed(2)}`;
      }], { fontSize: 11, color: baseColor, anchorX: 'right' });
      setTimeout(() => {
        const svg = containerRef.current?.querySelector('svg');
        const inner = containerRef.current?.innerHTML;
        console.log('[JXG] After 200ms. SVG present:', !!svg, 'svg dims:', svg ? `${svg.getAttribute('width')}x${svg.getAttribute('height')}` : 'n/a', 'innerHTML length:', inner?.length);
      }, 200);
    } catch (e) {
      console.error('[JXG] Init failed:', e);
      if (containerRef.current) {
        containerRef.current.textContent = `Diagram error: ${(e as Error).message}`;
        containerRef.current.classList.add('p-4', 'text-xs', 'text-rose-500');
      }
    }
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 100 && height > 100) {
          console.log('[JXG] ResizeObserver fired with', width, 'x', height, '- reinit if needed');
        }
      }
    });
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [isDark]);
  return <div ref={containerRef} className="w-full h-72 min-w-[280px] border border-gray-200 dark:border-white/10 rounded-md bg-white dark:bg-zinc-900" />;
};

const InteractiveCoordinatePlane = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isDark = useDarkMode();
  useEffect(() => {
    console.log('[JXG] InteractiveCoordinatePlane mount.');
    if (!containerRef.current) return;
    const r = containerRef.current.getBoundingClientRect();
    console.log('[JXG] Container size:', r.width, 'x', r.height);
    try {
      const board = JXG.JSXGraph.initBoard(containerRef.current, {
        boundingbox: [-5, 5, 5, -5],
        axis: true,
        showCopyright: false,
        showNavigation: false,
        pan: { needShift: true },
        zoom: { wheel: false, needShift: true }
      });
      console.log('[JXG] Board created. Children:', board.objectsList.length);
      const baseColor = isDark ? '#e2e8f0' : '#0f172a';
      board.create('axis', [[0, 0], [1, 0]], { strokeColor: baseColor, tickColor: baseColor, labelColor: baseColor });
      board.create('axis', [[0, 0], [0, 1]], { strokeColor: baseColor, tickColor: baseColor, labelColor: baseColor });
      const f = (_x: number) => 0.5 * _x;
      board.create('functiongraph', [f, -5, 5], { strokeColor: '#6366f1', strokeWidth: 2 });
      const p1 = board.create('glider', [-3, 0, board.defaultAxes.x], { name: 'A', color: '#ef4444', size: 4 });
      const p2 = board.create('glider', [2, 0, board.defaultAxes.x], { name: 'B', color: '#10b981', size: 4 });
      board.create('segment', [p1, p2], { strokeColor: baseColor, strokeWidth: 2 });
      board.create('text', [-4.8, 4.6, () => {
        const midX = (p1.X() + p2.X()) / 2;
        const midY = f(midX);
        return `A(${p1.X().toFixed(2)}, 0)    B(${p2.X().toFixed(2)}, 0)    midpoint → f(${midX.toFixed(2)}) = ${midY.toFixed(2)}`;
      }], { fontSize: 11, color: baseColor });
      setTimeout(() => {
        const svg = containerRef.current?.querySelector('svg');
        console.log('[JXG] After 200ms. SVG present:', !!svg, 'svg dims:', svg ? `${svg.getAttribute('width')}x${svg.getAttribute('height')}` : 'n/a');
      }, 200);
    } catch (e) {
      console.error('[JXG] Init failed:', e);
      if (containerRef.current) {
        containerRef.current.textContent = `Diagram error: ${(e as Error).message}`;
        containerRef.current.classList.add('p-4', 'text-xs', 'text-rose-500');
      }
    }
  }, [isDark]);
  return <div ref={containerRef} className="w-full h-72 min-w-[280px] border border-gray-200 dark:border-white/10 rounded-md bg-white dark:bg-zinc-900" />;
};

const MathFieldInput = ({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) => {
  const ref = useRef<MathfieldElement | null>(null);
  useEffect(() => {
    const mf = ref.current;
    if (!mf) return;
    const handler = () => {
      const latex = mf.getValue('latex');
      onChange(latex || mf.value);
    };
    mf.addEventListener('input', handler);
    return () => { mf.removeEventListener('input', handler); };
  }, [onChange]);
  return (
    <MathField
      ref={ref as unknown as React.Ref<unknown>}
      value={value}
      placeholder={placeholder}
      className="w-full min-h-[2.5rem] max-h-32 border-0 bg-transparent text-sm text-slate-800 dark:text-zinc-200"
      style={{ outline: 'none' }}
    />
  );
};

const MessageContent = ({ content, role }: { content: string; role: 'user' | 'assistant' }) => {
  if (role === 'user') {
    return <div className="text-sm leading-relaxed">{formatText(content)}</div>;
  }

  const combinedRegex = /%%(PLOT|DIAGRAM)%%([\s\S]*?)%%/g;
  const segments: { type: 'text' | 'plot' | 'diagram'; content: string }[] = [];
  let lastIndex = 0;
  let match;
  while ((match = combinedRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', content: content.slice(lastIndex, match.index) });
    }
    const type = match[1] === 'PLOT' ? 'plot' : 'diagram';
    const rawJsonStr = match[2];
    console.log(`[AI Parser] Found ${type} block:`, rawJsonStr);
    segments.push({ type, content: rawJsonStr });
    lastIndex = combinedRegex.lastIndex;
  }
  if (lastIndex < content.length) {
    segments.push({ type: 'text', content: content.slice(lastIndex) });
  }
  if (segments.length === 1 && segments[0].type === 'text') {
    const codeBlockRegex = /```(?:json|js|ts)?\s*(\{[\s\S]*?"type"[\s\S]*?\})\s*```/g;
    const fallback: { type: 'text' | 'diagram'; content: string }[] = [];
    let li = 0;
    let m2;
    while ((m2 = codeBlockRegex.exec(content)) !== null) {
      if (m2.index > li) fallback.push({ type: 'text', content: content.slice(li, m2.index) });
      fallback.push({ type: 'diagram', content: m2[1] });
      li = codeBlockRegex.lastIndex;
    }
    if (fallback.length > 0) {
      if (li < content.length) fallback.push({ type: 'text', content: content.slice(li) });
      segments.length = 0;
      fallback.forEach(f => segments.push(f as { type: 'text' | 'plot' | 'diagram'; content: string }));
    }
  }
  if (segments.length === 0) {
    segments.push({ type: 'text', content });
  }

  return (
    <div className="space-y-4">
      {segments.map((seg, segIdx) => {
        if (seg.type === 'plot') {
          try {
            console.log(`[AI Parser] Attempting to parse plot spec:`, seg.content);
            // Clean up possible markdown code block wrappers
            let cleaned = seg.content.trim();
            if (cleaned.startsWith('```json')) cleaned = cleaned.replace(/^```json\n?/, '');
            else if (cleaned.startsWith('```')) cleaned = cleaned.replace(/^```\n?/, '');
            if (cleaned.endsWith('```')) cleaned = cleaned.replace(/\n?```$/, '');
            cleaned = cleaned.trim();
            
            const spec = JSON.parse(cleaned) as PlotSpec;
            return <PlotBlock key={segIdx} spec={spec} />;
          } catch (e) {
            console.error(`[AI Parser] Failed to parse plot JSON! Error:`, e, `Raw content:`, seg.content);
            return <div key={segIdx} className="text-red-500 border border-red-500/30 bg-red-500/10 rounded p-3 text-xs my-2">⚠️ Failed to render plot (Invalid data format)</div>;
          }
        }
        if (seg.type === 'diagram') {
          try {
            console.log(`[AI Parser] Attempting to parse diagram spec:`, seg.content);
            // Clean up possible markdown code block wrappers
            let cleaned = seg.content.trim();
            if (cleaned.startsWith('```json')) cleaned = cleaned.replace(/^```json\n?/, '');
            else if (cleaned.startsWith('```')) cleaned = cleaned.replace(/^```\n?/, '');
            if (cleaned.endsWith('```')) cleaned = cleaned.replace(/\n?```$/, '');
            cleaned = cleaned.trim();

            const spec = JSON.parse(cleaned) as DiagramSpec;
            return <DiagramBlock key={segIdx} spec={spec} />;
          } catch (e) {
            console.error(`[AI Parser] Failed to parse diagram JSON! Error:`, e, `Raw content:`, seg.content);
            return <div key={segIdx} className="text-red-500 border border-red-500/30 bg-red-500/10 rounded p-3 text-xs my-2">⚠️ Failed to render diagram (Invalid data format)</div>;
          }
        }
        return <SectionRenderer key={segIdx} text={seg.content} />;
      })}
    </div>
  );
};

const SectionRenderer = ({ text }: { text: string }) => {
  const sections = text.split(/(?=^#{1,6}\s|^\*\*.*?\*\*|^References:?|^Final Answer:?|^Step-by-Step Explanation:?|^Key Insight:?|^Mistakes Found:?|^Practice Problems:?|^Step \d+:)/gmi);

  return (
    <>
      {sections.map((section, idx) => {
        const trimmed = section.trim();
        if (!trimmed) return null;

        const lowerTrimmed = trimmed.toLowerCase();
        const isHeaderBlock = /^#{1,6}\s/.test(trimmed) ||
                              lowerTrimmed.startsWith('final answer') ||
                              lowerTrimmed.startsWith('step-by-step explanation') ||
                              lowerTrimmed.startsWith('references') ||
                              lowerTrimmed.startsWith('key insight') ||
                              lowerTrimmed.startsWith('mistakes found') ||
                              lowerTrimmed.startsWith('practice problems') ||
                              /^step \d+:/i.test(trimmed);

        if (isHeaderBlock || /^\*\*.*?\*\*[:]/.test(trimmed) || /^\*\*.*?\*\*/.test(trimmed)) {
          const lines = trimmed.split('\n');
          const headerRaw = lines[0];
          const header = headerRaw.replace(/^#{1,6}\s+/, '').replace(/^\*\*/, '').replace(/\*\*$/, '').replace(/:$/, '').trim();
          const body = lines.slice(1).join('\n').trim();

          let headerColor = "text-slate-900 dark:text-zinc-100";
          let bodyStyle = "text-slate-600 dark:text-zinc-300";
          let containerStyle = "";

          if (header.toLowerCase().includes("final answer")) {
            headerColor = "text-emerald-600 dark:text-emerald-400";
            containerStyle = "bg-emerald-50/50 dark:bg-emerald-900/10 p-3 border border-emerald-100 dark:border-emerald-500/20";
            bodyStyle = "text-slate-800 dark:text-zinc-200 font-medium";
          } else if (header.toLowerCase().includes("references") || header.toLowerCase().includes("resources")) {
            headerColor = "text-blue-600 dark:text-blue-400";
            containerStyle = "bg-blue-50/50 dark:bg-blue-900/10 p-4 border border-blue-100 dark:border-blue-500/20 text-sm font-medium";
          } else if (header.toLowerCase().includes("key insight")) {
            headerColor = "text-violet-600 dark:text-violet-400";
            containerStyle = "bg-violet-50/50 dark:bg-violet-900/10 p-3 border border-violet-100 dark:border-violet-500/20";
          } else if (header.toLowerCase().includes("mistake")) {
            headerColor = "text-red-600 dark:text-red-400";
            containerStyle = "bg-red-50/50 dark:bg-red-900/10 p-3 border border-red-100 dark:border-red-500/20";
          } else if (header.toLowerCase().includes("practice problems")) {
            headerColor = "text-amber-600 dark:text-amber-400";
            containerStyle = "bg-amber-50/50 dark:bg-amber-900/10 p-3 border border-amber-100 dark:border-amber-500/20";
          } else if (/^step \d+:/i.test(header)) {
            headerColor = "text-indigo-600 dark:text-indigo-400";
            containerStyle = "bg-indigo-50/30 dark:bg-indigo-900/5 p-2.5 border-l-2 border-indigo-400";
          }

          return (
            <div key={idx} className={containerStyle}>
              {header && <h3 className={`text-xs font-bold uppercase tracking-wider mb-2 ${headerColor}`}>{header}</h3>}
              <div className={`text-sm leading-relaxed ${bodyStyle}`}>
                {splitBodyByDisplayMath(body)}
              </div>
            </div>
          );
        }

        return (
          <div key={idx} className="text-sm leading-relaxed text-slate-700 dark:text-zinc-300">
            {splitBodyByDisplayMath(trimmed)}
          </div>
        );
      })}
    </>
  );
};

interface PaperTapeEntry {
  q: string;
  r: string;
  t: string;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isImage?: boolean;
  isAudio?: boolean;
  audioUrl?: string;
  isExplanation?: boolean;
  isPractice?: boolean;
}

const AudioMessageBubble = ({ audioUrl }: { audioUrl?: string }) => {
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio(audioUrl);
      audioRef.current.addEventListener('ended', () => { setPlaying(false); setCurrentTime(0); });
      audioRef.current.addEventListener('timeupdate', () => setCurrentTime(audioRef.current!.currentTime));
      audioRef.current.addEventListener('loadedmetadata', () => setDuration(audioRef.current!.duration));
    }
    return () => { audioRef.current?.pause(); audioRef.current = null; };
  }, [audioUrl]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); } else { audioRef.current.play(); }
    setPlaying(!playing);
  };

  const pct = duration > 0 ? (currentTime / duration) * 100 : 0;
  const fmt = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex items-center gap-2.5 min-w-[200px]">
      <button onClick={togglePlay} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center shrink-0 transition-colors">
        {playing ? <Pause className="w-3.5 h-3.5 fill-current" /> : <Play className="w-3.5 h-3.5 fill-current ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="h-1 bg-white/20 rounded-full overflow-hidden">
          <div className="h-full bg-white rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-[9px] text-white/60 mt-1 font-mono">{fmt(currentTime)} / {duration > 0 ? fmt(duration) : '0:00'}</p>
      </div>
    </div>
  );
};

const IndependentAICalculatorView = () => {
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [imageBlob, setImageBlob] = useState<Blob | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [tutorMode, setTutorMode] = useState(false);
  const [useMathField, setUseMathField] = useState(false);
  const [difficulty, setDifficulty] = useState<Difficulty>('medium');
  const [practiceActive, setPracticeActive] = useState(false);
  const [activeMode, setActiveMode] = useState<string | null>(null);
  const [paperTape, setPaperTape] = useState<PaperTapeEntry[]>([]);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const tutorThreadIdRef = useRef<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  useEffect(() => {
    return () => {
      if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    };
  }, [imagePreviewUrl]);

  function buildSystemPrompt(): string {
    const modeFlags: string[] = [];
    modeFlags.push(`\n\n### CURRENT SETTINGS\n- Difficulty: ${difficulty.toUpperCase()}\n- Tutor Mode: ${tutorMode ? 'ACTIVE' : 'OFF'}`);
    if (tutorMode) {
      modeFlags.push(`\n⚠️ TUTOR MODE IS ON — You MUST follow the TUTOR MODE rules in the system prompt above. Ask ONE guiding question. Do NOT give the answer. Guide the student through Socratic questioning.`);
    }
    if (practiceActive) {
      modeFlags.push(`- Practice Mode: ACTIVE — generate practice problems (no answers) per the user's request`);
    }
    if (activeMode === 'check') {
      modeFlags.push(`- Check Mode: ACTIVE — the user is pasting their attempted solution. Analyze it line by line. Find sign errors, dropped parentheses, distribution mistakes, exponent rules, fraction operations, order of operations, off-by-one errors, unit mistakes. For each: WHAT went wrong, WHY, HOW to fix it.`);
    } else if (activeMode === 'plot') {
      modeFlags.push(`- Plot Mode: ACTIVE — the user will provide a function to graph. Provide a %%PLOT%% marker with the function. Include zeros, intercepts, and key features.`);
    } else if (activeMode === 'diagram') {
      modeFlags.push(`- Diagram Mode: ACTIVE — the user will describe a diagram. Output the appropriate %%DIAGRAM%% marker. Choose from: right-triangle, unit-circle, coordinate-plane, triangle, circle, number-line, vector, box-plot, bar-chart, normal-distribution, bearing, locus, scale-drawing, bearing-elevation, bearing-journey.`);
    } else if (activeMode === '3d') {
      modeFlags.push(`- 3D Mode: ACTIVE — the user wants a rotatable 3D shape. Output a %%DIAGRAM%% with type 3d-cube, 3d-sphere, 3d-cylinder, 3d-cone, or 3d-rectangular-prism. Include volume and surface area.`);
    } else if (activeMode === 'matrix') {
      modeFlags.push(`- Matrix Mode: ACTIVE — the user will provide a matrix. Use \\begin{pmatrix}...\\end{pmatrix} LaTeX. Offer: determinant, inverse, transpose, eigenvalues, rank, RREF, solve Ax=b, multiplication.`);
    } else if (activeMode === 'word') {
      modeFlags.push(`- Word Problem Mode: ACTIVE — the user will describe a word problem. Label each step explicitly: Step 1: Identify given information, Step 2: Set up equations, Step 3: Solve, Step 4: Verify with units. Carry units through every calculation.`);
    }
    if (paperTape.length > 0) {
      modeFlags.push(`\n### USER'S RECENT CALCULATION HISTORY (Paper Tape)\nThe user has performed these recent calculations. When they ask about their history ("what was the average", "show only integrals today", etc.), use this data:\n${JSON.stringify(paperTape)}`);
    }
    return SYSTEM_PROMPT + modeFlags.join('');
  }

  function extractPaperTapeFromResponse(raw: string): { cleaned: string; tape: PaperTapeEntry[] } {
    const tapeMatch = raw.match(/```paper-tape\n([\s\S]*?)\n```/);
    if (!tapeMatch) return { cleaned: raw, tape: [] };
    try {
      const parsed = JSON.parse(tapeMatch[1]) as PaperTapeEntry[];
      const cleaned = raw.replace(tapeMatch[0], '').trim();
      return { cleaned, tape: parsed };
    } catch {
      return { cleaned: raw, tape: [] };
    }
  }

  async function handleSearch(overrideQuery?: string, overrideAudioBlob?: Blob, overrideImageBlob?: Blob) {
    const textToSearch = (overrideQuery ?? query).trim();
    const activeAudio = overrideAudioBlob ?? null;
    const activeImage = overrideImageBlob ?? imageBlob;

    if (!textToSearch && !activeAudio && !activeImage) return;

    const activeMedia = activeImage || activeAudio || null;

    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: activeImage
        ? `📷 Image uploaded${textToSearch ? ': ' + textToSearch : ' — please solve the math in this image.'}`
        : textToSearch || '',
      isImage: !!activeImage,
      isAudio: !!activeAudio && !activeImage,
      audioUrl: activeAudio ? URL.createObjectURL(activeAudio) : undefined,
    };
    setMessages(prev => [...prev, userMsg]);
    setQuery('');
    setIsLoading(true);

    // ── TUTOR MODE BRANCH ──
    // Route through the LangGraph-powered tutor engine instead of the
    // generic system-prompt Socratic rules. The engine maintains its
    // own per-session state (diagnosis, hint ladder, misconceptions).
    if (tutorMode) {
      try {
        const userId = auth.currentUser?.uid;
        const convLog: { role: 'user' | 'assistant'; content: string }[] = [
          ...messages.map(m => ({ role: m.role, content: m.content })),
          { role: 'user' as const, content: textToSearch || (activeImage ? '[Image uploaded]' : '[Voice message]') },
        ];

        const turn = await runTutorTurn({
          problem: textToSearch || 'See attached image/audio.',
          conversation: convLog,
          difficulty,
          mediaBlob: activeMedia,
          contextId: userId,
          threadId: tutorThreadIdRef.current ?? undefined,
        });
        tutorThreadIdRef.current = turn.threadId;

        const aiMsg: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: turn.message || 'I had trouble forming a question. Could you rephrase that?',
          isExplanation: false,
          isPractice: false,
        };
        setMessages(prev => [...prev, aiMsg]);
      } catch (err) {
        console.error('Tutor engine failed:', err);
        setMessages(prev => [...prev, {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: 'The tutor had a hiccup. Please try again.',
        }]);
      } finally {
        setIsLoading(false);
      }
      return;
    }
    // ── END TUTOR MODE BRANCH ──

    try {
      const historyContext = messages.map(m => `${m.role.toUpperCase()}: ${m.content}`).join('\n');
      const fullPrompt = `${buildSystemPrompt()}\n\n--- CONVERSATION HISTORY ---\n${historyContext}\n\n--- NEW QUERY ---\n${textToSearch || (activeImage ? 'Solve the math shown in this image.' : 'Transcribe and solve what was said.')}`;

      const userId = auth.currentUser?.uid;
      const rawResponse = await geminiMultimodalGenerate(fullPrompt, activeMedia, userId);
      console.log(`[AI Raw Response] =>`, rawResponse);
      const { cleaned: afterTape, tape: extractedTape } = extractPaperTapeFromResponse(rawResponse || '');
      const finalResponse = afterTape.trim() || 'I apologise, but I could not generate a response. Please try again.';

      if (extractedTape.length > 0) {
        setPaperTape(prev => [...extractedTape, ...prev].slice(0, 50));
      } else if (textToSearch && !tutorMode) {
        setPaperTape(prev => [
          { q: textToSearch.slice(0, 80), r: finalResponse.slice(0, 120), t: new Date().toISOString() },
          ...prev
        ].slice(0, 50));
      }

      const aiMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: finalResponse,
        isExplanation: textToSearch.toLowerCase().startsWith('explain') || textToSearch.toLowerCase().includes('explain in detail'),
        isPractice: practiceActive
      };
      setMessages(prev => [...prev, aiMsg]);
      setPracticeActive(false);
      setActiveMode(null);
    } catch (error) {
      console.error('AI calculator search failed:', error);
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'I encountered an error processing your request. Please check your connection and try again.'
      }]);
    } finally {
      setIsLoading(false);
    }
  }

  function handleExplainLast() {
    const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant');
    if (!lastAssistant) return;
    handleSearch(`Explain the previous solution in much more detail. Show every step, the reasoning behind each step, alternative approaches, common pitfalls, and add a "Key Insight" section summarizing the underlying principle.`);
  }

  function handlePractice() {
    setPracticeActive(true);
    handleSearch(`Generate 5 practice problems at ${difficulty} difficulty. Mix problem types if possible. Do not include answers.`);
  }

  function handleCheckWork() {
    const lastQ = [...messages].reverse().find(m => m.role === 'user')?.content;
    const prefix = lastQ ? `Check my work on: "${lastQ.slice(0, 120)}"\n\nMy attempt:\n` : 'Check my work:\n\nMy attempt:\n';
    setQuery(prefix);
    setActiveMode('check');
  }

  function handleResources() {
    handleSearch('Suggest 2-3 short, reputable learning resources (Khan Academy, 3Blue1Brown, MIT OCW, etc.) with real URLs for the most recent topic we discussed. Format each as [Name - Topic](url).');
  }

  function handleFormula() {
    handleSearch('Look up the formula for the topic I asked about. Insert it in display LaTeX, then explain every variable/term clearly with units. Add 1-2 related or alternative formulas if relevant.');
  }

  function handlePlot() {
    setQuery('Plot the function: ');
    setActiveMode('plot');
  }

  function handleDiagram() {
    setQuery('Draw a diagram of: ');
    setActiveMode('diagram');
  }

  function handle3D() {
    setQuery('Show me a 3D cube/sphere/cone/cylinder: ');
    setActiveMode('3d');
  }

  function handleMatrix() {
    setQuery('Matrix operations on:\n');
    setActiveMode('matrix');
  }

  function handleFinancial() {
    handleSearch('Financial or Statistics calculation. I need help with:\n\n**Finance:** compound interest, loan payments, present/future value, ROI, NPV, amortization.\n**Statistics:** mean, median, standard deviation, confidence interval, hypothesis test, regression, normal distribution.\n\nPlease provide the values and I will solve it step by step with formulas and LaTeX.');
  }

  function handleWordProblem() {
    setQuery('Solve this word problem step-by-step:\n\n');
    setActiveMode('word');
  }

  function handlePaperTape() {
    const summary = paperTape.length === 0
      ? 'No calculations in history yet.'
      : `Here is my recent calculation history (${paperTape.length} entries):\n${JSON.stringify(paperTape)}.\n\nAnalyze it and answer: what patterns do you see, what's the average, are there any errors? Include your structured history block (with a paper-tape code fence) for me to see.`;
    handleSearch(summary);
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSearch();
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        handleSearch('', blob);
        stream.getTracks().forEach(t => t.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Could not access microphone.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
    }
  };

  const toggleRecording = () => {
    if (isRecording) stopRecording();
    else startRecording();
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file (JPG, PNG, WEBP, etc.)');
      return;
    }
    const blob = new Blob([file], { type: file.type });
    setImageBlob(blob);
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    setImagePreviewUrl(URL.createObjectURL(blob));
    e.target.value = '';
  };

  const hasAssistantMessages = messages.some(m => m.role === 'assistant');

  const [backVisible, setBackVisible] = useState(true);
  const backTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showBack = useCallback(() => {
    setBackVisible(true);
    if (backTimer.current !== null) clearTimeout(backTimer.current);
    backTimer.current = setTimeout(() => setBackVisible(false), 5000);
  }, []);

  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => document.addEventListener(e, showBack));
    showBack();
    return () => {
      events.forEach(e => document.removeEventListener(e, showBack));
      if (backTimer.current !== null) clearTimeout(backTimer.current);
    };
  }, [showBack]);

  return (
    <div className="h-full flex flex-col relative bg-gray-50 dark:bg-[#020617]">
      {/* Back button — toggles to standard calculator, or falls back to utilities */}
      <button
        onClick={() => {
          const fromStandard = (location.state as { from?: string } | null)?.from === 'calculator';
          navigate(fromStandard ? '/dashboard/utilities/calculator' : '/dashboard/utilities');
        }}
        title="Go back"
        aria-label="Go back"
        className={`sticky top-0 -ml-3 z-20 self-start h-7 w-7 flex items-center justify-center rounded-full bg-zinc-100 dark:bg-white/5 text-zinc-500 hover:text-zinc-900 dark:hover:text-white border border-zinc-200 dark:border-white/10 transition-all duration-300 ${
          backVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75 pointer-events-none'
        }`}
      >
        <ArrowLeft className="w-3.5 h-3.5" />
      </button>

      {/* Header */}
      <div className="flex-none flex items-center justify-between px-5 py-2 border-b border-gray-200 dark:border-white/5 bg-white dark:bg-[#020617] z-10 gap-2">
        <div className="flex items-center gap-2 pl-8 min-w-0">
          <Sparkles className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
          <span className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-700 dark:text-zinc-300 truncate">
            AI Calculator
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={() => {
              setTutorMode(v => {
                if (v) tutorThreadIdRef.current = null; // turning off: reset tutor session
                return !v;
              });
            }}
            title={tutorMode ? 'Tutor mode is ON' : 'Tutor mode is OFF'}
            className={`flex items-center gap-1 px-1.5 py-1 text-[9px] font-black uppercase tracking-widest transition-colors border ${
              tutorMode
                ? 'bg-violet-600 border-violet-500 text-white hover:bg-violet-500'
                : 'border-gray-200 dark:border-white/10 text-slate-400 dark:text-slate-500 hover:text-violet-500 dark:hover:text-violet-400'
            }`}
          >
            <GraduationCap className="w-3 h-3" />
            Tutor
          </button>
          <div className="flex items-center border border-gray-200 dark:border-white/10">
            {(['easy', 'medium', 'hard'] as Difficulty[]).map(d => (
              <Tooltip key={d}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setDifficulty(d)}
                    title={`Set difficulty to ${d}`}
                    className={`px-1.5 py-1 text-[9px] font-black uppercase tracking-widest transition-colors ${
                      difficulty === d
                        ? 'bg-indigo-600 text-white'
                        : 'text-slate-400 dark:text-slate-500 hover:text-indigo-500 dark:hover:text-indigo-400'
                    }`}
                  >
                    {d === 'easy' ? 'E' : d === 'medium' ? 'M' : 'H'}
                  </button>
                </TooltipTrigger>
                <TooltipContent><p className="text-[10px]">{d === 'easy' ? 'Easy: simpler numbers with more guidance' : d === 'medium' ? 'Medium: standard textbook problems' : 'Hard: challenging multi-concept problems'}</p></TooltipContent>
              </Tooltip>
            ))}
          </div>
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto scroll-smooth min-h-0">
        <div className="max-w-3xl mx-auto p-3 sm:p-5 space-y-4">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center py-6 px-2">
              <div className="w-10 h-10 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 flex items-center justify-center mb-3">
                <Calculator className="w-5 h-5 text-indigo-500" />
              </div>
              <h2 className="text-lg font-black uppercase tracking-tighter text-slate-900 dark:text-white mb-1">
                Solve anything with AI
              </h2>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-600 max-w-sm mb-4">
                Type, speak, or upload a photo. Get LaTeX solutions, plots, formulas, and step-by-step tutoring.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-w-lg w-full text-left">
                {[
                  "Calculate the integral of x² from 0 to 1",
                  "Show a 3D cube with side 2 and find its volume",
                  "Draw an interactive unit circle I can drag",
                  "Compound interest: $1000 at 5% for 10 years"
                ].map((hint, i) => (
                  <button
                    key={i}
                    onClick={() => handleSearch(hint)}
                    className="p-2 text-left text-[9px] font-bold uppercase tracking-wider border border-gray-200 dark:border-white/10 bg-white dark:bg-white/[0.02] hover:bg-gray-50 dark:hover:bg-white/5 hover:border-indigo-500/40 dark:hover:border-indigo-500/30 transition-all text-slate-500 dark:text-slate-500 hover:text-slate-800 dark:hover:text-slate-300"
                  >
                    {hint}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4 pb-2">
              {tutorMode && (
                <div className="flex items-center justify-between bg-violet-50 dark:bg-violet-900/10 border border-violet-200 dark:border-violet-500/20 px-3 py-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <GraduationCap className="w-3 h-3 text-violet-500 shrink-0" />
                    <span className="text-[9px] font-black uppercase tracking-widest text-violet-600 dark:text-violet-400">
                      Tutor Mode — guiding with questions
                    </span>
                  </div>
                  <button
                    onClick={() => setTutorMode(false)}
                    className="text-violet-400 hover:text-red-500 transition-colors ml-2 shrink-0"
                    title="Exit tutor mode"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}

              {messages.map((msg) => (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="w-6 h-6 bg-indigo-600/10 dark:bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center shrink-0 mt-1">
                      <Sparkles className="w-3 h-3 text-indigo-500" />
                    </div>
                  )}

                  <div className={`text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'max-w-[85%] sm:max-w-[75%] px-3 py-2.5 bg-indigo-600 text-white whitespace-pre-wrap'
                      : 'max-w-[85%] sm:max-w-[75%] px-3 py-2.5 bg-white dark:bg-[#0a1024] border border-gray-200 dark:border-white/8 text-slate-800 dark:text-zinc-200'
                  }`}>
                    {msg.isAudio ? (
                      <AudioMessageBubble audioUrl={msg.audioUrl} />
                    ) : (
                      <MessageContent content={msg.content} role={msg.role} />
                    )}

                    {msg.role === 'assistant' && (
                      <div className="flex items-center gap-2.5 mt-2.5 pt-2 border-t border-gray-100 dark:border-white/5 flex-wrap">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={handleExplainLast}
                              disabled={isLoading}
                              className="flex items-center gap-1 text-[8px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 hover:text-indigo-500 dark:hover:text-indigo-400 transition-colors disabled:opacity-40"
                            >
                              <BookOpen className="w-2.5 h-2.5" />
                              Explain
                            </button>
                          </TooltipTrigger>
                          <TooltipContent><p className="text-[10px]">Get a deeper explanation with every step, reasoning, and common pitfalls</p></TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={handleResources}
                              disabled={isLoading}
                              className="flex items-center gap-1 text-[8px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 hover:text-blue-500 dark:hover:text-blue-400 transition-colors disabled:opacity-40"
                            >
                              <Video className="w-2.5 h-2.5" />
                              Resources
                            </button>
                          </TooltipTrigger>
                          <TooltipContent><p className="text-[10px]">Suggest learning resources for the most recent topic</p></TooltipContent>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}

              {isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex gap-3 justify-start"
                >
                  <div className="w-6 h-6 bg-indigo-600/10 dark:bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center shrink-0 mt-1">
                    <Sparkles className="w-3 h-3 text-indigo-500" />
                  </div>
                  <div className="px-3 py-2.5 bg-white dark:bg-[#0a1024] border border-gray-200 dark:border-white/8 text-slate-500">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  </div>
                </motion.div>
              )}

              <div ref={chatEndRef} />
            </div>
          )}
        </div>
      </div>

      {/* Input area */}
      <div className="flex-none bg-gradient-to-t from-gray-50 via-gray-50 to-transparent dark:from-[#020617] dark:via-[#020617] pt-3">
        <div className="max-w-3xl mx-auto px-3 pb-2">
          {/* Quick action chips row 1 */}
          <div className="flex items-center gap-1 mb-1.5 flex-wrap">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handlePractice}
                  disabled={isLoading}
                  className="flex items-center gap-1 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest border border-amber-200 dark:border-amber-500/20 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/10 transition-colors disabled:opacity-40"
                >
                  <ListChecks className="w-2.5 h-2.5" />
                  Practice
                </button>
              </TooltipTrigger>
              <TooltipContent><p className="text-[10px]">Generate 5 practice problems at the current difficulty level</p></TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleCheckWork}
                  disabled={isLoading}
                  className="flex items-center gap-1 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest border border-red-200 dark:border-red-500/20 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors disabled:opacity-40"
                >
                  <Wrench className="w-2.5 h-2.5" />
                  Check
                </button>
              </TooltipTrigger>
              <TooltipContent><p className="text-[10px]">Paste your solution and get AI feedback on errors and sign mistakes</p></TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleFormula}
                  disabled={isLoading}
                  className="flex items-center gap-1 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest border border-cyan-200 dark:border-cyan-500/20 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-900/10 transition-colors disabled:opacity-40"
                >
                  <Library className="w-2.5 h-2.5" />
                  Formula
                </button>
              </TooltipTrigger>
              <TooltipContent><p className="text-[10px]">Look up the relevant formula for the last topic discussed</p></TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handlePlot}
                  disabled={isLoading}
                  className="flex items-center gap-1 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest border border-pink-200 dark:border-pink-500/20 text-pink-600 dark:text-pink-400 hover:bg-pink-50 dark:hover:bg-pink-900/10 transition-colors disabled:opacity-40"
                >
                  <LineChart className="w-2.5 h-2.5" />
                  Plot
                </button>
              </TooltipTrigger>
              <TooltipContent><p className="text-[10px]">Graph a function with sensible defaults and key features shown</p></TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleDiagram}
                  disabled={isLoading}
                  className="flex items-center gap-1 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest border border-fuchsia-200 dark:border-fuchsia-500/20 text-fuchsia-600 dark:text-fuchsia-400 hover:bg-fuchsia-50 dark:hover:bg-fuchsia-900/10 transition-colors disabled:opacity-40"
                >
                  <Square className="w-2.5 h-2.5" />
                  Diagram
                </button>
              </TooltipTrigger>
              <TooltipContent><p className="text-[10px]">Draw a geometry diagram — supports triangles, circles, vectors, and more</p></TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handle3D}
                  disabled={isLoading}
                  className="flex items-center gap-1 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest border border-rose-200 dark:border-rose-500/20 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/10 transition-colors disabled:opacity-40"
                >
                  <Box className="w-2.5 h-2.5" />
                  3D
                </button>
              </TooltipTrigger>
              <TooltipContent><p className="text-[10px]">Show a rotatable 3D shape with volume and surface area calculations</p></TooltipContent>
            </Tooltip>
            <button
              onClick={() => setUseMathField(v => !v)}
              title={useMathField ? 'Switch to plain text input' : 'Switch to math keypad input (mathlive)'}
              className={`flex items-center gap-1 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest border transition-colors ${
                useMathField
                  ? 'bg-indigo-600 border-indigo-500 text-white'
                  : 'border-indigo-200 dark:border-indigo-500/20 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/10'
              }`}
            >
              <Sigma className="w-2.5 h-2.5" />
              Math
            </button>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleMatrix}
                  disabled={isLoading}
                  className="flex items-center gap-1 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors disabled:opacity-40"
                >
                  <BarChart3 className="w-2.5 h-2.5" />
                  Matrix
                </button>
              </TooltipTrigger>
              <TooltipContent><p className="text-[10px]">Perform matrix operations — determinant, inverse, transpose, eigenvalues, and more</p></TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleFinancial}
                  disabled={isLoading}
                  className="flex items-center gap-1 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest border border-emerald-200 dark:border-emerald-500/20 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/10 transition-colors disabled:opacity-40"
                >
                  <TrendingUp className="w-2.5 h-2.5" />
                  Fin/Stats
                </button>
              </TooltipTrigger>
              <TooltipContent><p className="text-[10px]">Financial or statistics calculations — compound interest, mean, hypothesis tests</p></TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleWordProblem}
                  disabled={isLoading}
                  className="flex items-center gap-1 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest border border-orange-200 dark:border-orange-500/20 text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/10 transition-colors disabled:opacity-40"
                >
                  <Train className="w-2.5 h-2.5" />
                  Word
                </button>
              </TooltipTrigger>
              <TooltipContent><p className="text-[10px]">Solve a word problem step-by-step with equations and unit verification</p></TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handlePaperTape}
                  disabled={isLoading}
                  className="flex items-center gap-1 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest border border-teal-200 dark:border-teal-500/20 text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/10 transition-colors disabled:opacity-40"
                >
                  <ScrollText className="w-2.5 h-2.5" />
                  Paper ({paperTape.length})
                </button>
              </TooltipTrigger>
              <TooltipContent><p className="text-[10px]">View your recent calculation history for patterns, averages, or errors</p></TooltipContent>
            </Tooltip>
            {hasAssistantMessages && (
              <button
                onClick={handleExplainLast}
                disabled={isLoading}
                className="flex items-center gap-1 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest border border-indigo-200 dark:border-indigo-500/20 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-colors disabled:opacity-40"
              >
                <BookOpen className="w-2.5 h-2.5" />
                Explain
              </button>
            )}
          </div>

          {/* Image preview */}
          {imagePreviewUrl && (
            <div className="mb-1.5 flex items-center gap-2 px-1">
              <div className="relative flex items-center gap-2 bg-white dark:bg-[#0a1024] border border-violet-300 dark:border-violet-500/30 px-2 py-1 max-w-xs">
                <img src={imagePreviewUrl} alt="Staged image" className="h-7 w-7 object-cover" />
                <div className="min-w-0">
                  <p className="text-[8px] font-black uppercase tracking-widest text-violet-600 dark:text-violet-400">Image ready</p>
                </div>
                <button
                  onClick={() => {
                    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
                    setImageBlob(null);
                    setImagePreviewUrl(null);
                  }}
                  className="ml-1 text-slate-400 hover:text-red-500 transition-colors"
                  title="Remove image"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}

          <div className={`flex gap-1.5 items-end bg-white dark:bg-[#0a1024] border transition-all ${
            isRecording
              ? 'border-red-500/60 shadow-[0_0_0_2px_rgba(239,68,68,0.15)]'
              : imageBlob
                ? 'border-violet-400/60 dark:border-violet-500/30 shadow-[0_0_0_2px_rgba(139,92,246,0.1)]'
                : tutorMode
                  ? 'border-violet-400/60 dark:border-violet-500/30 shadow-[0_0_0_2px_rgba(139,92,246,0.1)]'
                  : 'border-gray-200 dark:border-white/10 focus-within:border-indigo-500/50 dark:focus-within:border-indigo-500/30 focus-within:shadow-[0_0_0_2px_rgba(99,102,241,0.1)]'
          }`}>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*"
              onChange={handleFileChange}
            />

            <button
              onClick={handleImageClick}
              title={imageBlob ? 'Replace image' : 'Upload a photo'}
              className={`h-9 w-9 flex items-center justify-center transition-colors shrink-0 ${
                imageBlob
                  ? 'text-violet-500 dark:text-violet-400'
                  : 'text-slate-400 dark:text-slate-600 hover:text-violet-500 dark:hover:text-violet-400'
              }`}
            >
              <Camera className="w-4 h-4" />
            </button>

            {useMathField ? (
              <MathFieldInput
                value={query}
                onChange={setQuery}
                placeholder={
                  tutorMode
                    ? 'Tutor mode on — try the next step yourself…'
                    : imageBlob
                      ? 'Add a question about this image…'
                      : 'Click to open the math keypad…'
                }
              />
            ) : (
              <Textarea
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  isRecording
                    ? 'Listening…'
                    : tutorMode
                      ? 'Tutor mode on — try the next step yourself…'
                      : imageBlob
                        ? 'Add a question about this image…'
                        : 'Type, speak, or click a chip above…'
                }
                className="min-h-[2rem] max-h-24 border-0 bg-transparent focus-visible:ring-0 resize-none py-2 px-0 text-sm text-slate-800 dark:text-zinc-200 placeholder:text-slate-400 dark:placeholder:text-slate-600"
                rows={1}
              />
            )}

            <div className="flex items-center gap-1 pr-1 pb-1 shrink-0">
              <button
                onClick={toggleRecording}
                title={isRecording ? 'Stop and send recording' : 'Start voice recording'}
                className={`h-7 w-7 flex items-center justify-center transition-all ${
                  isRecording
                    ? 'text-white bg-red-500 hover:bg-red-600 rounded-full animate-pulse shadow-lg shadow-red-500/30'
                    : 'text-slate-400 dark:text-slate-600 hover:text-indigo-500 dark:hover:text-indigo-400'
                }`}
              >
                {isRecording ? (
                  <Square className="w-3 h-3 fill-current" />
                ) : (
                  <Mic className="w-3 h-3" />
                )}
              </button>

              <button
                onClick={() => handleSearch()}
                title="Send"
                disabled={(!query.trim() && !imageBlob) || isLoading}
                className="h-7 w-7 flex items-center justify-center bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95"
              >
                {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              </button>
            </div>
          </div>

          <p className="text-[8px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-700 mt-1 text-center">
            AI may be inaccurate · verify critical calcs · {difficulty.toUpperCase()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default IndependentAICalculatorView;
